import {
    action,
    computed,
    makeObservable,
    observable,
    override,
    reaction,
    runInAction,
} from "mobx";
import type { GridRowModel, GridRowParams } from "@mui/x-data-grid-pro";
import BaseObjectStore, {
    BaseObjectStoreParams,
    BaseRefreshOptions,
    RefreshResult,
} from "../BaseObjectStore";
import {
    DefinitionChartTypeValueMapping,
    RenderModeType,
    SignalsEndpoints,
    type ISignalsChartResponse,
} from "../../SignalsModels";
import * as SignalsModels from "components/Signals/SignalsModels";
import { DatePickerComponentStore } from "stores/ComponentStores/DatePickerComponentStore";
import { toIsoByDateReference } from "utils/DateTimeUtils";
import moment from "moment";
import type { ReactElement } from "react";
import {
    temporalGrain,
    THE_MICHAEL_GRAIN,
} from "components/Signals/BaseObjects/DataAggregation";
import { v4 as uuidv4 } from "uuid";
import { SignalReportDatum } from "models/Reporting/ReportDatum";
import {
    dataPointTooltipBuilder,
    getEligibleConversations,
    getScoredConversationCount,
} from "./DataPointTooltips";
import { relabelDateGroupingBuckets } from "./dateBucketLabels";
import { resolveDateGroupingTemporalGrain } from "../Drilldown/groupingDrilldown";
import {
    SignalChartDatum,
    getSignalChartPointKey,
    signalDataToGoogleFormat,
    type SignalChartPointIdentity,
    type XValueType,
} from "utils/ReportDataToGoogleFormat";
import {
    getEndpointBehaviorConfig,
} from "../_DataEndpointConfig";
import { resolveOrdering } from "../_DataEndpointConfig/OrderingRuleResolver";
import { dropAllZeroGroupedSeries } from "../_DataEndpointConfig/Transforms";
import OrgOptionsStore from "stores/OrgOptionsStore";
import type { GoogleChartWrapper } from "react-google-charts";
import AISummaryStore from "./ChartFeatureStores/AISummaryStore";
import ZoomStore from "./ChartFeatureStores/ZoomStore";
import ChartWidthStore from "./ChartFeatureStores/ChartWidthStore";
import OrderRulesStore, {
    toOrderableBucket,
    type OrderableBucket,
} from "./ChartFeatureStores/OrderRulesStore";
import GroupingStore from "./ChartFeatureStores/GroupingStore";
import AcxDataGridStore from "components/UI/AcxDataGrid/AcxDataGridStore";
import type { SignalsParsedVizOptions } from "components/Signals/SignalsModels";
import MessageStore from "components/ManagerInteractions/Stores/MessageStore";
import Papa from "papaparse";
import { FieldNode } from "components/CSVExport/fieldNodes";
import { getSignalsColumns } from "../_DataEndpointConfig/DataGridBuilders";
import type { GridConfig, PaginationConfig, SkillGrouping, TransformContext } from "../_DataEndpointConfig/Types";
import {
    getClassifierOptionsForCategory,
    normalizeClassifierCategoryId,
} from "./ChartControls/classifierUtils";
import {
    getPublishedModelResultsClassifierIdFromEndpoint,
    resolveSelectionDefaults,
    SelectionDefaultDependency,
    SelectionDefaultsPatch,
} from "../_DataEndpointConfig/DefaultSelectionsSetters";
import {
    ApplicationFilterItemsStore,
    type FilterKey,
} from "stores/ApplicationFilters/ApplicationFilterItemsStore";

/**
 * Translation from exportable field name to the Application Filter behind it, for the export fields
 * that have one. Needed because the server names these columns for the query aliases they bind to,
 * which are not filter keys.
 *
 * `ThemeViaTopics` and `Theme` are both labelled "Primary Theme" by the server, which states no
 * status of its own because status is viewer-specific and only the client knows what this viewer can
 * see. Translating to the filter key is what lets the field picker reach that answer; whether a
 * marker actually appears is decided by `withDeprecationMarker`, not by membership here.
 */
const EXPORT_FIELD_FILTER_KEYS: Readonly<Record<string, FilterKey>> = {
    ThemeViaTopics: "topics",
};

export enum FeedbackType {
    Dislike = 1,
    ItsOkay = 2,
    Like = 3,
}


export const CHART_PAGE_SIZE = 7;
export const LINE_CHART_PAGE_SIZE = 5;
export const COMPACT_CHART_PAGE_SIZE = 5;
export const MAX_CHART_SELECTIONS = 100;
const PERCENT_REMAINDER_SERIES_LABEL = "Other";
const PERCENT_REMAINDER_SERIES_IDENTIFIER = "__percent-remainder-series__";
const PERCENT_REMAINDER_EPSILON = 1e-10;

type SummaryPaginationAxis = "xAxis" | "series";
type SummaryDatumDescriptor = {
    id?: string;
    label: string;
};
type SummarySeriesColumn = SummaryDatumDescriptor & {
    columnIndexes: number[];
};

class ChartCardStore extends BaseObjectStore<SignalReportDatum[], SignalsModels.SignalsRequest | null, ChartCachePayload> {
    // === Rendering & chart configuration ===
    /** Current render mode (count or percent) used to display chart values. */
    @observable currentRenderMode: RenderModeType = RenderModeType.Count;
    /** Whether previous-period comparison is currently enabled. */
    @observable comparePrevEnabled: boolean = false;
    /** Chart type derived from the chart definition (bar, line, etc.). */
    @observable chartType: SignalsModels.SignalsChartType;
    /** SQL query backing the chart when provided. */
    @observable sqlQuery?: string;
    /**
     * The user's live skill grouping choice. Unset until they pick one, so the saved vizOptions value
     * applies. updateDefaultSelectedIds persists it, like selectedIds.
     */
    @observable private skillGroupingSelection?: SkillGrouping;

    /**
     * Whether the chart has data it can actually render. TreeMaps read
     * `displayedData` and never produce countData / percentData, so they are
     * checked separately. Chart uses this to decide between the canvas and the
     * empty state, and ChartCard uses it to keep the data grid hidden while
     * the chart has nothing to show.
     */
    @computed get hasRenderableChartData(): boolean {
        if (
            this.dataEndpoint ===
            SignalsEndpoints.additionalReports.TotalConversationsDonutId
        ) {
            return this.hasRenderableTotalConversationsDonutData();
        }

        return this.chartType === "TreeMap"
            ? this.displayedData != null && this.displayedData.length > 0
            : this.data != null &&
                  this.countData != null &&
                  this.percentData != null;
    }

    @computed get isChartDataLoading(): boolean {
        return (
            this.isRefreshing ||
            this.comparePrevLoading ||
            this.isWaitingOnRequiredOptions ||
            !this.isChartRenderingReady
        );
    }

    @computed get isChartErrorPresent(): boolean {
        return !!(
            this.error ||
            (this.chartType === "TreeMap" && this.zoomStore.error)
        );
    }

    @computed get isDisplayingRenderableChartData(): boolean {
        return (
            this.hasRenderableChartData &&
            !this.isChartDataLoading &&
            !this.isChartErrorPresent &&
            !this.groupDeleted &&
            !this.groupAccessDenied
        );
    }

    /** Scale type is sourced from vizOptions so it persists on custom dashboards. */
    @computed get scaleType(): "log" | "mirrorLog" | undefined {
        return this.parsedVizOptions?.scaleType;
    }

    /**
     * The skill grouping this chart reports on, for endpoints with a skill grouping selector.
     * The live selection wins; otherwise the saved vizOptions value, then the endpoint's default.
     */
    @computed get selectedSkillGrouping(): SkillGrouping | undefined {
        const selector = this.config.skillGroupingSelector;
        if (!selector) return undefined;
        return (
            this.skillGroupingSelection ??
            this.parsedVizOptions?.skillGrouping ??
            selector.defaultValue
        );
    }

    /** Y-axis label, suffixed with "(log scale)" when a log scale is active. */
    @computed get displayYTitle(): string {
        return this.scaleType ? `${this.yTitle} (log scale)` : this.yTitle;
    }

    /** The chart's measured width and the layout choices that follow from it. */
    readonly widthStore = new ChartWidthStore();

    /**
     * Chart types that show fewer bars or series when compact. Line paginates
     * by series and already defaults to five, so the cap only affects lines
     * showing more. "StackedVerticalBar" is absent because it renders with
     * `chartType` "VerticalBar".
     */
    static readonly chartTypesWithLimitedBarsWhenNarrow: SignalsModels.SignalsChartType[] =
        ["VerticalBar", "HorizontalBar", "Line"];

    /**
     * Compact charts cap the rendered bar and series count by paging what is
     * already held. AI summaries follow `isSummaryPaginated`, not this, so a
     * chart's summary does not depend on its width.
     */
    @computed
    get isBarCountLimitedByWidth(): boolean {
        return (
            this.widthStore.isCompact &&
            ChartCardStore.chartTypesWithLimitedBarsWhenNarrow.includes(
                this.chartType,
            )
        );
    }

    static readonly chartTypesThatCanChangeScaleType: SignalsModels.SignalsChartType[] = ["VerticalBar", "HorizontalBar", "Line"]
    @computed get canChangeScaleType() { return ChartCardStore.chartTypesThatCanChangeScaleType.includes(this.chartType) }

    // === AI summaries ===
    /** Whether this chart instance is configured to show its Spotlight surface. */
    readonly showSpotlight: boolean;
    /** Whether this chart instance is configured to show its KBo analysis surface. */
    readonly showAnalyzeWithKBo: boolean;
    /** Store used to manage AI-generated summaries for the chart. */
    @observable aiSummaryStore: AISummaryStore | null = null;
    /** Store used to resolve and apply chart order rules. */
    orderRulesStore: OrderRulesStore;
    /** Store that owns what this chart is grouped by and which way it is oriented. */
    groupingStore: GroupingStore;

    // === Zoom (TreeMap + any future zoom-capable chart) ===
    /**
     * Always instantiated, even on non-zoomable charts. Whether zoom actually
     * fires is determined at point-menu time by `viewLevel` + the row's
     * `hasChildren` marker — there's no capability gate on the store itself.
     */
    @observable zoomStore!: ZoomStore;
    private zoomAbortController: AbortController = new AbortController();

    // === Data & render state ===
    /** Latest request payload used to fetch chart data. */
    /** Label displayed on the chart's x-axis. */
    @observable xTitle: string = "";
    /** Label displayed on the chart's y-axis. */
    @observable yTitle: string = "";
    /** Google chart wrapper reference for exporting or resizing operations. */
    @observable chartWrapper: GoogleChartWrapper | null = null;
    /** Raw chart response saved for reuse across transformations. */
    private rawResponse: ISignalsChartResponse | null = null;
    /** True when this chart's classifier group was deleted (drives the "group deleted" empty state). */
    @observable groupDeleted: boolean = false;
    /** True when the shared group has no models the user can view (drives the "no access" empty state). */
    @observable groupAccessDenied: boolean = false;

    // === Pagination & lifecycle state ===
    /** Current page index for paginated charts. */
    @observable currentPage: number = 0;
    /** Data identifiers currently visible on the active page (paginated charts only). */
    private dataIdsInView: string[] = [];
    /** Data identifiers used to generate the most recent AI summary. */
    private dataIdsUsedForSummary: string[] | undefined = undefined;
    /** Indicates whether initialization has finished. */
    @observable hasCompletedInitialization: boolean | undefined;
    /** Flags refresh attempts triggered before initialization completed. */
    @observable pendingRefreshWhileInitializing: boolean | undefined;
    /** Dependencies currently blocking deterministic selection-default resolution. */
    @observable private waitingSelectionDefaultDependencies: SelectionDefaultDependency[] = [];
    /** Suppresses default persistence while resolver-driven selection patches are being applied. */
    private suppressDefaultPersistence: boolean = false;
    /** Tracks dependencies this chart has requested at least once to avoid waiting forever on resolved-empty options. */
    private requestedSelectionDefaultDependencies: Set<SelectionDefaultDependency> = new Set();
    /** In-flight dependency loads keyed by dependency type. */
    private selectionDefaultDependencyPromises = new Map<
        SelectionDefaultDependency,
        Promise<void>
    >();
    /** Prevents scheduling duplicate standalone follow-up refreshes while dependency loads are pending. */
    private pendingStandaloneDefaultFollowup: Promise<void> | null = null;
    /** Tracks whether we've completed initial default resolution so viz defaults only win on first pass. */
    private hasResolvedInitialSelectionDefaults: boolean = false;

    // === Compare previous period ===
    /** Data set returned for compare-previous mode. */
    @observable comparePrevData: SignalReportDatum[] | null = null;
    /** Whether compare-previous data is in-flight. */
    @observable comparePrevLoading: boolean = false;
    /** Cache key used for the latest compare-previous fetch. */
    private comparePrevCacheKey: string | null = null;

    // === Data caching ===
    /** Signature of filters used for the latest chart fetch. */
    private lastFiltersSignature: string | null = null;
    private bypassComparePrevCacheOnce: boolean = false;
    /** Shared cache of exportable field definitions keyed by chart endpoint. */
    private static exportableFieldsCache = new Map<
        string,
        SignalsModels.ExportableFieldsResponse
    >();
    /** Shared in-flight requests for exportable field definitions keyed by chart endpoint. */
    private static exportableFieldsRequest = new Map<
        string,
        Promise<SignalsModels.ExportableFieldsResponse>
    >();

    // === Selection & filtering ===
    /** Currently selected ids for filterable entities (topics, modules, etc.). */
    @observable selectedIds: Record<SelectedIdKey, string | string[] | undefined> = {
        llmClassifier: undefined,
        classifierCategory: undefined,
        classifiers: undefined,
        question: undefined,
        module: undefined,
        modules: undefined,
        questions: undefined,
        tags: undefined,
        topics: undefined,
        themes: undefined,
        classifierGroup: undefined,
    };
    /** Organization-wide options store used to populate selectors. */
    optionsStore: OrgOptionsStore;
    /** Selection type required by the chart (topics, modules, tags, etc.). */
    @observable selectionType: SignalsModels.ISignalsSelectionTypes;
    /** Series visibility driven by data grid selection (when enabled). */
    @observable private gridVisibleSeries?: string[];

    // === Data grid ===
    /** Data grid store used when a chart also renders tabular data. */
    @observable dataGridStore: AcxDataGridStore | null = null;
    @observable private pendingGridSelection?: Array<string | number>;
    @observable dataGridSetupComplete: boolean = false;
    private skipNextDataGridRefresh: boolean = false;

    // === Export ===
    /** Exportable fields available for CSV exports. */
    @observable exportableFields: SignalsModels.ExportableFieldsResponse | null =
        null;
    /** Indicates whether exportable fields are currently loading. */
    @observable exportableFieldsLoading: boolean = false;

    // === Initialization & lifecycle ===
    /**
     * Creates a new chart store, derives initial configuration, and wires reactions.
     * @param params - Base object definition, root store, options, and refresh behavior.
     */
    constructor({
        baseObject,
        rootStore,
        options,
        initialData,
        refreshOnCreate,
    }: BaseObjectStoreParams<
        SignalReportDatum[],
        SignalsModels.ISignalsChartDefinition
    >) {
        // Defer refresh-on-create until after chart store initialization so selection defaults are ready
        const shouldRefreshOnCreate = refreshOnCreate ?? false;
        super({
            baseObject,
            rootStore,
            options,
            initialData,
            refreshOnCreate: false,
        });

        if (!this.dataEndpoint) {
            throw new Error(
                `ChartCardStore must have a valid data endpoint. Chart ID: ${this.id}`,
            );
        }

        this.hasCompletedInitialization = this.hasCompletedInitialization ?? false;
        this.pendingRefreshWhileInitializing =
            this.pendingRefreshWhileInitializing ?? false;

        makeObservable(this);
        this.optionsStore = rootStore.getStore(OrgOptionsStore);
        // These properties are specifically using definition values instead of the helper functions
        this.currentRenderMode =
            baseObject.defaultRenderMode === RenderModeType.Count
                ? RenderModeType.Count
                : RenderModeType.Percent;
        this.chartType =
            DefinitionChartTypeValueMapping[baseObject.typeOfChart];
        this.selectionType =
            SignalsModels.DefinitionDataSelectionTypeMapping[
            baseObject.typeOfDataSelectedInChart
            ];
        this.sqlQuery = baseObject.sqlQuery;
        this.showSpotlight = baseObject.showSpotlight;
        this.showAnalyzeWithKBo = baseObject.showAnalyzeWithKBo;

        this.config = getEndpointBehaviorConfig({
            dataEndpoint: this.dataEndpoint,
            selectionType: this.selectionType,
        });
        this.orderRulesStore = new OrderRulesStore(this);
        this.groupingStore = new GroupingStore(this);

        if (this.showSpotlight || this.showAnalyzeWithKBo) {
            this.aiSummaryStore = new AISummaryStore({
                baseObjectId: this.id,
                rootStore: this.rootStore,
            });
        }

        this.zoomStore = new ZoomStore({
            host: {
                dataEndpoint: this.dataEndpoint,
                baseObjectId: this.id,
                buildZoomRequestBody: (zoomPath) =>
                    this.buildZoomRequestBody(zoomPath),
                getZoomCacheKeyEnvelope: () => ({
                    filters: this.getCacheFiltersPart(),
                    selections: this.getCacheSelectionsPart(),
                    extras: this.getCacheExtraParts(),
                }),
                getAbortSignal: () => this.zoomAbortController.signal,
            },
            rootStore: this.rootStore,
        });

        // AI summary plumbing for zoom-capable charts. The first TreeMap that
        // ships will have spotlight = false, so this won't actually render an
        // AI summary panel — but the per-level "summary is outdated when the
        // user zooms" behavior is wired now so flipping spotlight on later
        // doesn't require revisiting ZoomStore. Cache-key keyed-by-zoomPath
        // is left for the consumer that actually turns spotlight on (the
        // AISummaryStore would need its own per-path map then). TODO when a
        // real TreeMap turns spotlight on: extend AISummaryStore to cache
        // summaries per zoom path.
        reaction(
            () => this.zoomStore.zoomPathIdentifiers.join("|"),
            () => {
                if (!this.aiSummaryStore) return;
                this.aiSummaryStore.setIsSummaryOutdated(true);
                this.aiSummaryStore.setIsSummaryRefreshable(true);
            },
        );

        this.initializeSelectionDefaults();
        reaction(
            () => [this.totalItems, this.pageSize, this.isPaginated],
            () => {
                if (!this.isPaginated || this.pageSize <= 0) return;
                const maxPage = this.maxPage;
                if (this.currentPage > maxPage) {
                    this.setPage(maxPage);
                }
            },
        );
        // Watch for selection changes and update defaultSelectedIds
        reaction(
            () =>
                JSON.stringify({
                    selectedIds: this.getSelectedIdsForVizDefaults(),
                    // Undefined for charts without a skill grouping selector, which JSON.stringify drops.
                    skillGrouping: this.selectedSkillGrouping,
                    selectedClassifierCategoryId:
                        this.selectionType === "Classifiers"
                            ? this.getSelectedClassifierCategoryId()
                            : undefined,
                }),
            () => {
                void this.updateDefaultSelectedIds();
            },
            { delay: 500 }, // Debounce to avoid excessive API calls
        );
        this.hasCompletedInitialization = true;

        if (this.pendingRefreshWhileInitializing) {
            this.pendingRefreshWhileInitializing = false;
            void this.runRefresh();
        }
        // Now that initialization is complete, honor the original refresh-on-create flag
        if (shouldRefreshOnCreate) {
            void this.runRefresh();
        }
    }

    // === Refresh lifecycle ===
    /**
     * Refreshes chart data while respecting initialization and default selection states.
     * @param forceRefresh - When true, bypasses caches for the next request cycle.
     * @returns Promise resolved once refresh completes or defers.
     */
    @override
    async runRefresh(
        forceRefresh: boolean = false,
        options?: ChartRefreshOptions,
    ): Promise<RefreshResult> {
        this.zoomAbortController.abort();
        this.zoomAbortController = new AbortController();

        // Reset zoom synchronously before the new fetch is scheduled. This is
        // the single broad rule that covers filter changes, manual Refresh,
        // selection changes, and any future trigger -- zoomPath is gone
        // before the next fetch starts, so there's no race between an
        // in-flight zoom response and the new top-level fetch.
        if (this.zoomStore?.isZoomed || this.zoomStore?.isFetching) {
            this.zoomStore.reset();
        }

        if (forceRefresh) {
            this.bypassComparePrevCacheOnce = true;
        }

        const refreshOrigin = options?.origin ?? "standalone";
        if (
            refreshOrigin === "standalone" &&
            !this.canAutoRefreshInCurrentContext()
        ) {
            return { status: "completed" };
        }

        const dataGridConfig = this.config.dataGrid;
        const skipDataGridRefresh = options?.skipDataGridRefresh ?? false;
        if (this.dataGridStore && dataGridConfig && !skipDataGridRefresh) {
            this.dataGridStore.setLoading(true);
        }

        if (!this.hasCompletedInitialization) {
            this.pendingRefreshWhileInitializing = true;
            return { status: "completed" };
        }

        const selectionResolution = this.resolveSelectionDefaultsForRefresh();

        if (selectionResolution.status === "waiting") {
            if (!this.isRefreshing) {
                this.markRefreshQueued();
            }

            // If module options are still loading, dashboard batches already know how to wait and
            // retry. Standalone refreshes usually continue, but charts with `moduleSelector` first
            // need the module catalog so they can choose from the endpoint's scoped pool (for
            // example, only Quality modules). Wait here instead of making an early request with no
            // valid module selected. This still reaches an empty state when no scoped modules exist
            // because the modules dependency resolves after the catalog load attempt completes.
            if (
                refreshOrigin === "dashboard-batch" ||
                this.config.moduleSelector
            ) {
                if (
                    refreshOrigin !== "dashboard-batch" &&
                    this.canAutoRefreshInCurrentContext()
                ) {
                    this.scheduleStandaloneSelectionDefaultsFollowup(
                        selectionResolution.waitFor,
                    );
                }

                return {
                    status: "deferred",
                    waitFor: selectionResolution.waitFor,
                };
            }

            if (this.canAutoRefreshInCurrentContext()) {
                this.scheduleStandaloneSelectionDefaultsFollowup(
                    selectionResolution.waitFor,
                );
            }
        }

        this.skipNextDataGridRefresh = skipDataGridRefresh;
        return super.runRefresh(forceRefresh, {
            origin: refreshOrigin,
        });
    }

    @override get isSummaryLoading(): boolean {
        return this.aiSummaryStore?.isSummaryLoading ?? false;
    }

    public preRefresh(): void {
        this.getChartState({ includeRequestId: false });
    }

    /**
     * Updates dependent UI state after a refresh, including data grid. 
     * Also creates drilldown store after refresh, if it's not already created.
     * @returns void
     */
    public postRefresh(): void {
        const dataGridConfig = this.config.dataGrid;
        if (dataGridConfig) {
            const shouldSkipGridRefresh =
                this.skipNextDataGridRefresh && this.dataGridStore;
            if (shouldSkipGridRefresh && this.dataGridStore) {
                this.skipNextDataGridRefresh = false;
                this.dataGridStore.setLoading(false);
                super.postRefresh();
                return;
            }
            this.skipNextDataGridRefresh = false;
            runInAction(() => {
                this.dataGridSetupComplete = false;
            });
            // create data grid store if not already made
            if (!this.dataGridStore) {
                this.dataGridStore = new AcxDataGridStore("dg-" + this.id);
            }
            this.dataGridStore.removeHeight = "54px";
            // build data grid using newest data
            const gridData = this.data ?? this.rawResponse?.data;
            if (!gridData) {
                runInAction(() => {
                    this.dataGridSetupComplete = true;
                });
                if (this.aiSummaryStore && this.isSummaryPaginated) {
                    this.syncAISummaryStateForCurrentPage();
                }
                return;
            }
            this.dataGridStore.columns = getSignalsColumns(dataGridConfig.gridColumnType); // base columns
            dataGridConfig.dataGridBuilder(this.dataGridStore, dataGridConfig, gridData, this);
            this.configureDataGridUi(dataGridConfig);
            runInAction(() => {
                this.dataGridSetupComplete = true;
            });
            if (this.pendingGridSelection !== undefined) {
                const selectionIds = this.pendingGridSelection;
                this.pendingGridSelection = undefined;
                this.applyDataGridSelection(selectionIds);
            } else if (this.shouldReplayDataGridSelection()) {
                this.applyDataGridSelection(
                    this.dataGridStore.selectedRowIds as Array<
                        string | number
                    >,
                );
            }
            this.dataGridStore.setLoading(false);
        }

        if (this.aiSummaryStore && this.isSummaryPaginated) {
            this.syncAISummaryStateForCurrentPage();
        }

        super.postRefresh();
    }


    // === Data grid ===

    /**
     * Applies UI configuration for the data grid.
     * @param dataGridConfig - Grid configuration describing UI behavior.
     * @returns void
     */
    @action
    private configureDataGridUi(dataGridConfig: GridConfig) {
        if (!this.dataGridStore) return;

        this.dataGridStore.setHideVertIcon(true);
        const selectionConfig = dataGridConfig.selection;
        this.dataGridStore.sortOnSelection = Boolean(selectionConfig);
        this.dataGridStore.onChange = undefined;
        this.dataGridStore.isRowSelectable = undefined;
        this.dataGridStore.isSelectionReadOnly = false;
        if (selectionConfig) {
            this.dataGridStore.checkboxSelection = true;
            this.dataGridStore.isSelectionReadOnly = !this.isEditable;
            this.dataGridStore.disableRowSelectionOnClick = !this.isEditable;
            this.dataGridStore.onChange = async () => {
                if (!this.dataGridStore) return;
                const nextVisibleSeries = selectionConfig.onSelect({
                    chartStore: this,
                    dataGridStore: this.dataGridStore,
                });
                this.setVisibleSeries(nextVisibleSeries);
                if (selectionConfig.behavior === "request") {
                    void this.runRefresh();
                }
            };
            const shouldLimitDataGridSelections =
                this.selectionType === "Modules" ||
                this.dataEndpoint ===
                SignalsEndpoints.trends.AverageScoreByQuestionOverTimeID;

            if (shouldLimitDataGridSelections) {
                this.dataGridStore.isRowSelectable = (
                    params: GridRowParams,
                ) => {
                    if (!this.dataGridStore) return false;
                    const selected = this.dataGridStore.selectedRowIds;
                    const selectedCount = selected.length ?? 0;
                    const alreadySelected = selected.includes(params.id);
                    return alreadySelected || selectedCount < this.getPageSelectionLimit();
                };
            }
        } else {
            this.dataGridStore.checkboxSelection = false;
            this.dataGridStore.disableRowSelectionOnClick = true;
            this.dataGridStore.removeAllSelectedRows();
            this.setVisibleSeries(undefined);
        }
    }

    /**
     * Filters data-grid rows to the current chart selections when the grid is display-only.
     * Selection grids intentionally always render all available options.
     */
    public getDataGridRowsForSelectionContext(rows: GridRowModel[]): GridRowModel[] {
        if (!this.config.dataGrid || this.config.dataGrid.selection) {
            return rows;
        }

        const selectedIds = this.getSelectedIdsForFiltering();
        if (!selectedIds.length) {
            return rows;
        }

        const selectedIdSet = new Set(selectedIds.map((id) => `${id}`));

        return rows.filter((row) => {
            const rowId = row?.id;
            if (rowId === undefined || rowId === null) {
                return false;
            }
            return selectedIdSet.has(`${rowId}`);
        });
    }

    /**
     * Applies selection filtering (when relevant) and chart pagination to data-grid rows.
     */
    public getPaginatedDataGridRows(rows: GridRowModel[]): GridRowModel[] {
        const rowsForSelection = this.getDataGridRowsForSelectionContext(rows);

        if (!this.config.dataGrid?.followPagination || this.pageSize <= 0) {
            return rowsForSelection;
        }

        const startIndex = this.currentPage * this.pageSize;
        const endIndex = startIndex + this.pageSize;
        return rowsForSelection.slice(startIndex, endIndex);
    }

    // === Data fetching & transformation ===
    protected override getCacheFiltersPart(): unknown {
        return this.getFiltersSignature();
    }

    protected override getCacheSelectionsPart(): unknown {
        return this.getSelectionsSignature();
    }

    protected override getCacheExtraParts(): unknown {
        return {
            comparePrevEnabled: this.comparePrevEnabled,
            skillGrouping: this.selectedSkillGrouping,
        };
    }

    protected override getCachePayload(): ChartCachePayload {
        return {
            rawResponse: this.rawResponse,
            xTitle: this.xTitle,
            sqlQuery: this.sqlQuery,
            comparePrevData: this.comparePrevData,
            comparePrevCacheKey: this.comparePrevCacheKey,
        };
    }

    protected override restoreFromCachePayload(payload: unknown): void {
        if (!payload || typeof payload !== "object") return;
        const cachePayload = payload as Partial<ChartCachePayload>;
        this.rawResponse = cachePayload.rawResponse ?? null;
        this.xTitle = cachePayload.xTitle ?? "";
        this.sqlQuery = cachePayload.sqlQuery;
        this.comparePrevData = cachePayload.comparePrevData ?? null;
        this.comparePrevCacheKey = cachePayload.comparePrevCacheKey ?? null;
        this.yTitle =
            this.currentRenderMode === RenderModeType.Percent
                ? "Percent"
                : "Count";
    }

    /**
     * Fetches chart data, leveraging caches when possible and transforming responses.
     * @param params - Contains abort signal to cancel in-flight requests.
     * @returns Transformed chart data rows ready for rendering.
     */
    public async getData({
        abortSignal,
    }: {
        abortSignal: AbortSignal;
    }): Promise<SignalReportDatum[]> {
        const bypassComparePrevCache = this.bypassComparePrevCacheOnce;
        const filtersSignature = this.getFiltersSignature();
        this.lastFiltersSignature = filtersSignature;
        const selectionSignature = this.getSelectionsSignature();
        const comparePrevCacheKey = this.buildComparePrevCacheKey(
            filtersSignature,
            selectionSignature,
        );

        try {
            if (this.aiSummaryStore) {
                this.aiSummaryStore.setIsSummaryOutdated(true);
                this.aiSummaryStore.setIsSummaryRefreshable(true);
            }

            const state = this.getChartState();

            if (!this.dataEndpoint) return [];

            const response = await this.signalsService.getChart(
                this.dataEndpoint,
                state,
                abortSignal,
            );

            this.rawResponse = response;
            this.groupDeleted = response.groupDeleted === true;
            this.groupAccessDenied = response.groupAccessDenied === true;
            this.xTitle = response.xTitle;
            this.yTitle =
                this.currentRenderMode === RenderModeType.Percent
                    ? "Percent"
                    : "Count";
            this.sqlQuery = response.sqlQuery;
            const transformed = this.applyConfiguredTransforms(
                response,
                this.comparePrevEnabled,
            );

            if (
                this.config.capabilities.comparePrev &&
                this.comparePrevEnabled &&
                (!this.comparePrevData ||
                    this.comparePrevCacheKey !== comparePrevCacheKey)
            ) {
                await this.getComparePrevData(filtersSignature, {
                    bypassCache: bypassComparePrevCache,
                });
            }

            return transformed.data;
        } finally {
            this.bypassComparePrevCacheOnce = false;
        }
    }

    /**
     * Snapshot of the zoom-path entries this chart is currently rendering at.
     * Satisfies the optional `zoomPath` field on DrilldownSource so the
     * drilldown filter pipeline can replay each entry through
     * `applyZoomEntryToFilters` before the current-selection `applyToFilters`
     * runs.
     */
    @computed get zoomPath() {
        return this.zoomStore?.zoomPath ?? [];
    }

    /**
     * Builds the request body for a `${dataEndpoint}/zoom` call. Reuses the
     * chart's normal request envelope (filters + selections + extras) and
     * appends the stateless zoom path. Each call generates a fresh requestId.
     */
    public buildZoomRequestBody(
        zoomPath: string[],
    ): SignalsModels.SignalsRequest & { zoomPath: string[] } {
        const state = this.getChartState();
        return { ...state, zoomPath };
    }

    /**
     * The dataset the chart canvas should render: zoom-level data when the
     * user has zoomed in, otherwise the base data. v1 only TreeMap reads
     * this; other chart-type cases continue to consume countData / percentData.
     *
     * During an in-flight zoom transition, `zoomStore.currentLevelData` keeps
     * the level we were just on (so a depth 1 → 2 fetch shows level-1 data
     * underneath the loading overlay). On the very first zoom from depth 0 →
     * depth 1, there's no prior `currentLevelData` yet, so we fall back to
     * `this.data` (the base level) — the canvas keeps showing the level the
     * user clicked from until the next-level response arrives, rather than
     * flickering to empty.
     */
    @computed get displayedData(): SignalReportDatum[] | null | undefined {
        if (this.zoomStore?.isZoomed) {
            return this.zoomStore.currentLevelData ?? this.data;
        }
        return this.data;
    }

    /**
     * SQL behind the currently visible level. When zoomed, this is whatever
     * the zoom endpoint echoed back; otherwise it's the chart's base SQL.
     * View SQL reads this rather than `sqlQuery` directly.
     */
    @computed get displayedSqlQuery(): string | undefined {
        if (this.zoomStore?.isZoomed) {
            return this.zoomStore.currentLevelSqlQuery ?? this.sqlQuery;
        }
        return this.sqlQuery;
    }

    /**
     * Builds the request payload for fetching chart data using current filters.
     * @returns A SignalsRequest payload ready for the API.
     */
    @action
    private getChartState({
        includeRequestId = true,
    }: {
        includeRequestId?: boolean;
    } = {}): SignalsModels.SignalsRequest {
        const datePickerStore = this.getActiveDatePickerStore();
        const filters = this.getMergedFiltersForRequest();
        const startDate = (filters.startDate as string | undefined) ?? "";
        const endDate = (filters.endDate as string | undefined) ?? "";
        const dateReferenceOption =
            filters.dateReferenceOption ?? datePickerStore.referenceOption;
        const { compareStart, compareEnd } =
            this.getComparePreviousPeriodForRange(
                startDate,
                endDate,
                dateReferenceOption,
            );

        const state: SignalsModels.SignalsRequest = {
            ...filters,
            startDate: startDate ?? "",
            endDate: endDate ?? "",
            dateReference: dateReferenceOption,
            dateReferenceOption,
            dateRangeLabel: filters.dateRangeLabel,
        };

        // Prevent stale compare-period keys from filters from leaking into
        // normal requests when compare previous is disabled.
        delete state.previousPeriodStartDate;
        delete state.previousPeriodEndDate;

        const daysInRange = moment(endDate)?.diff(startDate, "days");
        state.aggregateDataByTemporalGrain = daysInRange > THE_MICHAEL_GRAIN;

        if (
            this.showSpotlight &&
            this.authStore.canUserView("AI Summaries") &&
            !this.aiSummaryStore?.isInitialSummaryFetched &&
            !this.isSummaryPaginated
        ) {
            state.requestAiSummary = true;
        } else {
            // Instead of automatically requesting the AI Summary, these charts will manually request
            // the AI Summary using the data rows in the chart
            state.requestAiSummary = false;
        }

        if (includeRequestId) {
            state.requestId = uuidv4();
        }

        if (this.config.capabilities.comparePrev && this.comparePrevEnabled && compareStart && compareEnd) {
            state.previousPeriodStartDate = compareStart;
            state.previousPeriodEndDate = compareEnd;
        }

        const endpointClassifierOverride =
            getPublishedModelResultsClassifierIdFromEndpoint(
                this.dataEndpoint,
            );

        if (endpointClassifierOverride) {
            state.llmClassifierId = endpointClassifierOverride;
        } else if (this.selectedIds.llmClassifier) {
            state.llmClassifierId = this.selectedIds.llmClassifier;
        }

        if (this.selectedIds.module) {
            state.moduleId = this.selectedIds.module;
        }

        if (this.selectedSkillGrouping !== undefined) {
            state.skillGrouping = this.selectedSkillGrouping;
        }

        if (this.selectedIds.question) {
            state.questionId = this.selectedIds.question;
        }

        if (this.selectedIds.questions) {
            state.questionIds = Array.isArray(this.selectedIds.questions)
                ? this.selectedIds.questions
                : [this.selectedIds.questions];
        }

        if (this.selectedIds.tags) {
            state.tagIds = Array.isArray(this.selectedIds.tags)
                ? this.selectedIds.tags
                : [this.selectedIds.tags];
        }

        // selectedIds.classifierGroup is the ordered path [root, ..., leaf];
        // the deepest (last) group is the chart's scope.
        if (this.selectedIds.classifierGroup) {
            const groupPath = Array.isArray(this.selectedIds.classifierGroup)
                ? this.selectedIds.classifierGroup
                : [this.selectedIds.classifierGroup];
            const deepestGroupId = groupPath[groupPath.length - 1];
            if (deepestGroupId) {
                state.classifierGroupId = deepestGroupId;
            }
        }

        const classifierScope = this.getClassifierSelectionScope();
        if (classifierScope.ids.length || classifierScope.hasCategoryFilter) {
            state.classifierIds = classifierScope.ids;
        }

        if (this.selectedIds.modules) {
            state.moduleIds = Array.isArray(this.selectedIds.modules)
                ? this.selectedIds.modules
                : [this.selectedIds.modules];
        }

        const shouldIncludeTopicIdsInRequest =
            this.selectionType === "Topics" &&
            this.dataEndpoint !==
            SignalsEndpoints.legacyTopicReports.PrimaryThemesId;

        if (shouldIncludeTopicIdsInRequest) {
            state.topicIds = Array.isArray(this.selectedIds.topics)
                ? this.selectedIds.topics
                : this.selectedIds.topics
                    ? [this.selectedIds.topics]
                    : [];
        }

        // Grouping request keys are forwarded from normalized vizOptions so backend
        // grouping logic can resolve filters/dimensions consistently.
        const grouping = this.comparePrevEnabled
            ? undefined
            : this.groupingStore.activeGrouping;
        if (grouping) {
            state.groupBy = grouping;
            state.swapGroupingAndX = this.groupingStore.swapGroupingAndX;
        }

        const patchedState = this.config.requestPatch(state);
        this.state = patchedState;
        return patchedState;
    }

    /**
     * Creates a stable string signature representing the current filter state.
     * @returns Serialized filter signature used for caching.
     */
    private getFiltersSignature(): string {
        const filters = this.getMergedFiltersForRequest();
        const startDate = (filters.startDate as string | undefined) ?? "";
        const endDate = (filters.endDate as string | undefined) ?? "";
        const dateReferenceOption =
            filters.dateReferenceOption ??
            this.getActiveDatePickerStore().referenceOption;
        const {
            compareStart: previousPeriodStartDate,
            compareEnd: previousPeriodEndDate,
        } = this.getComparePreviousPeriodForRange(
            startDate,
            endDate,
            dateReferenceOption,
        );
        const daysInRange =
            startDate && endDate ? moment(endDate)?.diff(startDate, "days") : 0;

        return this.stringifyNormalizedCacheValue({
            ...filters,
            startDate,
            endDate,
            previousPeriodStartDate,
            previousPeriodEndDate,
            dateReferenceOption,
            dateRangeLabel: filters.dateRangeLabel,
            aggregateDataByTemporalGrain: daysInRange > THE_MICHAEL_GRAIN,
            vizOptions: this.groupingStore.getCacheSignature(),
        });
    }

    /**
     * Converts selected date ranges into ISO strings respecting reference options.
     * @param dateStore - Date picker store containing the source dates.
     * @returns Array of ISO strings for start, end, compare start, and compare end.
     */
    getFormattedDates(
        dateStore: DatePickerComponentStore = this.getActiveDatePickerStore(),
    ): (string | undefined)[] {
        const dates = toIsoByDateReference(
            dateStore.beginDate,
            dateStore.endDate,
            dateStore.referenceOption,
        );

        const comparePrevDateStrings = toIsoByDateReference(
            this.comparePrevDates?.compareStart,
            this.comparePrevDates?.compareEnd,
            dateStore.referenceOption,
        );

        return [
            dates.beginDate,
            dates.endDate,
            comparePrevDateStrings.beginDate,
            comparePrevDateStrings.endDate,
        ];
    }

    /**
     * Calculates the date range to use for compare-previous requests.
     * @returns Date objects for the comparison window or undefined when unavailable.
     */
    @computed get comparePrevDates() {
        const activeDatePickerStore = this.getActiveDatePickerStore();
        const start = activeDatePickerStore.beginDate;
        const end = activeDatePickerStore.endDate;
        if (!start || !end) return undefined;

        const dayDifference = moment(end).diff(moment(start), "days");
        const compareStart = moment(start)
            .subtract(dayDifference + 1, "days")
            .startOf("date");
        const compareEnd = moment(end)
            .subtract(dayDifference + 1, "days")
            .endOf("date");
        return { compareStart, compareEnd };
    }

    /**
     * Returns the date picker whose values should drive chart requests. This prioritizes
     * the quick filters store so quick date selections affect outbound requests.
     */
    private getActiveDatePickerStore(): DatePickerComponentStore {
        return (
            this.applicationFiltersStore.quickFiltersStore?.datePickerStore ??
            this.applicationFiltersStore.datePickerStore!
        );
    }

    /**
     * Indicates whether required options for a selection type are still loading.
     * @returns True when option dependencies have not finished loading.
     */
    @computed
    get isWaitingOnRequiredOptions() {
        return this.getRequiredOptionsLoadingState();
    }

    /**
     * Determines if selection defaults are unresolved for the current selection type.
     * @returns True when defaults must still be resolved before fetching data.
     */
    @computed
    get isWaitingOnSelectionDefaults() {
        return this.waitingSelectionDefaultDependencies.length > 0;
    }

    // === Compare previous data ===
    /**
     * Fetches and caches comparison-period data when compare-prev is enabled.
     * @param filtersSignature - Optional signature of filters to cache results against.
     * @returns Promise resolved after compare-previous data loads.
     */
    @action
    private async getComparePrevData(
        filtersSignature?: string,
        options?: { bypassCache?: boolean },
    ) {
        if (!this.dataEndpoint) return;

        const bypassCache = options?.bypassCache ?? false;
        const activeFiltersSignature =
            filtersSignature ?? this.getFiltersSignature();
        const selectionSignature = this.getSelectionsSignature();
        const cacheKey = this.buildComparePrevCacheKey(
            activeFiltersSignature,
            selectionSignature,
        );

        const cached = bypassCache
            ? undefined
            : this.signalsEndpointCacheStore.get<SignalReportDatum[]>({
                endpoint: this.dataEndpoint,
                key: cacheKey,
                scope: "comparePrev",
            });

        if (cached) {
            this.comparePrevData = cached;
            this.comparePrevCacheKey = cacheKey;
            return;
        }

        if (this.comparePrevLoading) return;
        this.comparePrevLoading = true;

        try {
            const state = this.getChartState();
            const stateStartDate =
                typeof state.startDate === "string"
                    ? state.startDate
                    : state.startDate
                        ? moment(state.startDate).toISOString()
                        : undefined;
            const stateEndDate =
                typeof state.endDate === "string"
                    ? state.endDate
                    : state.endDate
                        ? moment(state.endDate).toISOString()
                        : undefined;
            const { compareStart, compareEnd } =
                this.getComparePreviousPeriodForRange(
                    stateStartDate,
                    stateEndDate,
                    state.dateReferenceOption,
                );
            state.previousPeriodStartDate = compareStart;
            state.previousPeriodEndDate = compareEnd;

            if (
                !state.previousPeriodStartDate ||
                !state.previousPeriodEndDate
            ) {
                return;
            }

            const response = await this.signalsService.getChart(
                this.dataEndpoint,
                state,
                new AbortController().signal,
            );
            const transformed = this.applyConfiguredTransforms(response, true);

            this.comparePrevData = transformed.data;
            this.comparePrevCacheKey = cacheKey;
            this.signalsEndpointCacheStore.set({
                endpoint: this.dataEndpoint,
                key: cacheKey,
                value: transformed.data,
                scope: "comparePrev",
            });
        } catch (_error) {
            this.comparePrevData = null;
        } finally {
            this.comparePrevLoading = false;
        }
    }

    private applyConfiguredTransforms(
        response: ISignalsChartResponse,
        isComparePrev: boolean,
    ): ISignalsChartResponse {
        // Ordering is resolved after endpoint transforms so those transforms can
        // reshape or strip data before the final sort runs.
        const swapGroupingAndX = this.groupingStore.swapGroupingAndX;

        const transformContext: TransformContext = {
            isComparePrev,
            groupingConfigured: this.groupingStore.isGrouped,
            swapGroupingAndX,
            ...this.orderRulesStore.getTransformContextOrderMaps(),
        };

        const transforms = this.groupingStore.isGrouped
            ? [
                  dropAllZeroGroupedSeries,
                  ...this.config.transforms,
              ]
            : this.config.transforms;

        const configTransformed = transforms.reduce(
            (acc, fn) => fn(acc, transformContext),
            response,
        );

        // Date buckets are relabelled before ordering so every later reader, meaning the axis, the
        // legend, tooltips, the data grid and CSV export, sees the span label rather than the
        // bucket-start label the backend sends. Ordering is unaffected: it compares identifiers,
        // which this leaves alone.
        const dateBucketLabelled = this.applyDateBucketLabels(configTransformed);

        const orderRules = this.orderRulesStore.getResolvedPreRenderOrderRules();

        return orderRules.reduce((acc, orderRule) => {
            const orderingTransform = resolveOrdering(orderRule);
            return orderingTransform
                ? orderingTransform(acc, transformContext)
                : acc;
        }, dateBucketLabelled);
    }

    /**
     * Relabels a date-grouped chart's buckets to the labels an over-time chart gives the same spans.
     *
     * The grain is the user's if they chose one and the range-derived one if they left it on auto.
     * `temporalGrain` is the same calculation, on the same range, that an over-time chart uses to
     * size its own buckets, which is what makes the two agree.
     *
     * A no-op for every other chart, including a chart grouped by anything else.
     */
    private applyDateBucketLabels(
        response: ISignalsChartResponse,
    ): ISignalsChartResponse {
        const grouping = this.groupingStore.activeGrouping;
        if (grouping?.kind !== "date" || !response.data?.length) {
            return response;
        }

        const grain = resolveDateGroupingTemporalGrain(
            grouping.grain,
            () => this.temporalGrain,
        );

        return {
            ...response,
            data: relabelDateGroupingBuckets(
                response.data,
                grain,
                this.startDate,
                this.endDate,
            ),
        };
    }

    /**
     * Re-derives the rendered data from the cached raw response using the current transforms and
     * order rules, without re-fetching. Bar ordering for non-grouped charts is baked into `this.data`
     * at fetch time (the reactive sorts in formatChartData only run when grouping is active), so this
     * lets callers preview an order-rule change (e.g. drag-reordering a grouped model chart's bars)
     * live. No-op when there's no cached response yet.
     */
    @action
    reapplyTransformsToCurrentData(): void {
        if (!this.rawResponse) return;
        const transformed = this.applyConfiguredTransforms(
            this.rawResponse,
            this.comparePrevEnabled,
        );
        this.data = transformed.data;
    }

    @computed get orientChartByXAxisLabel(): string {
        const rawYTitle = this.rawResponse?.yTitle?.trim();
        const normalizedRawYTitle = rawYTitle?.toLowerCase();
        const usableRawYTitle =
            normalizedRawYTitle && !["count", "percent"].includes(normalizedRawYTitle)
                ? rawYTitle
                : undefined;

        return (
            [
                this.xTitle,
                this.rawResponse?.xTitle,
                usableRawYTitle,
                this.getFallbackXAxisLabel(),
                "X-Axis",
            ]
                .map((label) => label?.trim())
                .find((label) => !!label) ?? "X-Axis"
        );
    }

    private getFallbackXAxisLabel(): string | undefined {
        switch (this.selectionType) {
            case "Classifiers":
                return "Classifier";
            case "Question":
            case "Questions":
                return "Question";
            case "Tags":
                return "Question Response";
            case "Modules":
                return "Module";
            case "Topics":
                return "Topic";
            case "HIPAA":
                return "HIPAA Compliance";
            case "Safety Event":
                return "Safety Event";
            case "Contact Type":
                return "Contact Type";
            case "LLM Classifier":
                return "Model";
            case "LLM Classifier Results":
                return "Response";
            case "Patient Journey":
                return "Patient Journey";
            case "Module":
                return "Module";
            case "Call Direction":
                return "Call Direction";
            case "LLM Classifier Extracted Metadata":
                return "Extracted Metadata";
            case "Classifier Group":
                return "Model";
            default:
                return undefined;
        }
    }

    private getComparePreviousPeriodForRange(
        startDate?: string,
        endDate?: string,
        dateReferenceOption = this.getActiveDatePickerStore().referenceOption,
    ): {
        compareStart?: string;
        compareEnd?: string;
    } {
        if (!startDate || !endDate) {
            return {
                compareStart: undefined,
                compareEnd: undefined,
            };
        }

        const start = moment(startDate);
        const end = moment(endDate);
        const dayDifference = end.diff(start, "days");
        const compareStartMoment = moment(start)
            .subtract(dayDifference + 1, "days")
            .startOf("date");
        const compareEndMoment = moment(end)
            .subtract(dayDifference + 1, "days")
            .endOf("date");
        const compareDateRange = toIsoByDateReference(
            compareStartMoment,
            compareEndMoment,
            dateReferenceOption,
        );

        return {
            compareStart: compareDateRange.beginDate,
            compareEnd: compareDateRange.endDate,
        };
    }

    // === Pagination settings ===
    /**
     * Builds the pagination configuration, forcing pagination on while grouped.
     * Grouped charts still inherit page-size and other pagination settings from
     * the base chart config, falling back to chart defaults when unspecified.
     * @returns Pagination configuration used by the chart.
     */
    @computed
    get paginationConfig(): PaginationConfig {
        const configured = this.configuredPaginationConfig;
        if (!this.isBarCountLimitedByWidth) return configured;

        const configuredPageSize = configured.pageSize;

        return {
            ...configured,
            paginated: true,
            pageSize:
                configuredPageSize && configuredPageSize > 0
                    ? Math.min(configuredPageSize, COMPACT_CHART_PAGE_SIZE)
                    : COMPACT_CHART_PAGE_SIZE,
        };
    }

    /**
     * Pagination from the chart's config and grouping, before a compact card
     * caps it.
     */
    @computed
    private get configuredPaginationConfig(): PaginationConfig {
        const basePagination = this.config.pagination ?? {
            paginated: false,
        };

        return this.groupingStore.isGrouped
            ? { ...basePagination, paginated: true }
            : basePagination;
    }

    /**
     * Whether the AI summary is scoped to the page in view. Only pagination
     * from the chart's config counts: a chart paged because its card is narrow
     * keeps the whole-chart summary it would have when wide.
     */
    @computed
    get isSummaryPaginated(): boolean {
        return this.configuredPaginationConfig.paginated;
    }

    /**
     * Indicates whether the current chart should render with pagination.
     * @returns True when pagination is enabled for the chart.
     */
    @computed
    get isPaginated(): boolean {
        return this.paginationConfig.paginated;
    }

    /**
     * Whether pagination controls should render (more than one page of data).
     * @returns True when paginated and data exceeds a single page.
     */
    @computed
    get shouldShowPagination(): boolean {
        if (!this.paginationConfig.paginated || this.pageSize <= 0) {
            return false;
        }

        return this.totalItems > this.pageSize;
    }

    /**
     * Page size derived from config or chart defaults.
     * @returns Number of items per page, or -1 when pagination is disabled.
     */
    @computed get pageSize() {
        if (!this.paginationConfig.paginated) return -1;

        const configuredPageSize = this.paginationConfig.pageSize;
        const defaultPageSize =
            this.chartType === "Line"
                ? LINE_CHART_PAGE_SIZE
                : CHART_PAGE_SIZE;

        if (configuredPageSize && configuredPageSize > 0) {
            return configuredPageSize;
        }

        return defaultPageSize;
    }

    // === Chart data accessors ===
    /**
     * Formatted chart data for count render mode.
     * @returns Formatted data table and series names, or null when unavailable.
     */
    @computed get formattedCountData() {
        return this.formatChartData(false);
    }

    /**
     * Formatted chart data for percent render mode.
     * @returns Formatted data table and series names, or null when unavailable.
     */
    @computed get formattedPercentData() {
        return this.formatChartData(true);
    }

    /**
     * Series labels present in the formatted chart data.
     * Derived from formatted data to avoid side effects in computed values.
     */
    @computed get seriesNames(): string[] {
        const formatted = this.formattedCountData ?? this.formattedPercentData;
        return formatted ? Array.from(formatted.seriesNames) : [];
    }

    /**
     * Series to render on line charts, based on grid selection configuration.
     * When no grid selection exists, default to all series.
     */
    @computed
    get visibleSeries(): string[] | undefined {
        if (this.config.dataGrid?.selection) {
            return this.gridVisibleSeries ?? [];
        }
        return this.seriesNames.length ? this.seriesNames : undefined;
    }

    /**
     * Indicates if a data grid selection has been applied (or is not required).
     * Used to delay rendering until default selections are applied.
     */
    @computed
    get isDataGridSelectionReady(): boolean {
        if (!this.config.dataGrid?.selection) return true;
        if (this.pendingGridSelection !== undefined) return false;
        return this.gridVisibleSeries !== undefined;
    }

    /**
     * Indicates if the chart can render, gated on data grid setup + selection readiness.
     */
    @computed
    get isChartRenderingReady(): boolean {
        if (!this.config.dataGrid?.selection) return true;
        return this.dataGridSetupComplete && this.isDataGridSelectionReady;
    }

    /**
     * Formatted count data with pagination applied when enabled.
     * @returns Chart data rows for count mode or null.
     */
    @computed get countData() {
        if (!this.formattedCountData) return null;

        return this.isPaginated
            ? this.applyPagination(this.formattedCountData.data)
            : this.formattedCountData.data;
    }

    /**
     * Formatted percent data with pagination applied when enabled.
     * @returns Chart data rows for percent mode or null.
     */
    @computed get percentData() {
        if (!this.formattedPercentData) return null;

        return this.isPaginated
            ? this.applyPagination(this.formattedPercentData.data)
            : this.formattedPercentData.data;
    }

    /**
     * Full, unpaginated count data for the chart.
     * @returns Full count dataset or null.
     */
    @computed get fullCountData() {
        return this.formattedCountData?.data ?? null;
    }

    /**
     * Full, unpaginated percent data for the chart.
     * @returns Full percent dataset or null.
     */
    @computed get fullPercentData() {
        return this.formattedPercentData?.data ?? null;
    }

    /** Resolves display content from the exact prepared point that produced its hover tooltip. */
    getPointTooltip(
        x: XValueType | null,
        series: string | null,
        seriesIdentifier?: string | null,
    ): ReactElement | undefined {
        if (!x) return undefined;
        const formatted =
            this.currentRenderMode === RenderModeType.Percent
                ? this.formattedPercentData
                : this.formattedCountData;
        if (!formatted) return undefined;

        // Donut selections identify a row without a column; its value is in the first series.
        const column =
            series === null && seriesIdentifier == null
                ? formatted.data[0][1]
                : undefined;
        return formatted.pointTooltips.get(
            getSignalChartPointKey({
                x,
                seriesIdentifier: seriesIdentifier ?? column?.id ?? "",
                seriesLabel: series ?? column?.label ?? "",
            }),
        );
    }

    getRawDatumForPoint(
        xId: string | Date | undefined,
        seriesId: string | null | undefined,
    ): SignalReportDatum | undefined {
        if (!this.data) return undefined;
        // Temporal charts pass a Date (parseXValue converts "Jan 15, 2024" → Date).
        // formatSummaryDateLabel converts it back to the same string using the chart's
        // temporal grain, so the comparison against datum.x is a plain string equality.
        const normalizedXId =
            xId instanceof Date ? this.formatSummaryDateLabel(xId) : xId;
        // Empty string means "no series" (single-series temporal charts emit "" as the column id).
        const effectiveSeriesId = seriesId || null;
        return this.data.find((d) => {
            const xMatch =
                (d.x_identifier ?? d.x) === normalizedXId ||
                d.x === normalizedXId;
            const seriesMatch =
                effectiveSeriesId != null
                    ? d.grouping_identifier === effectiveSeriesId ||
                      d.grouping === effectiveSeriesId
                    : !d.grouping;
            return xMatch && seriesMatch;
        });
    }

    /**
     * Reads tooltip display text from the raw datum behind a rendered point. This keeps extra hover
     * text in metadata instead of overloading chart labels or series names.
     */
    private getPointMetadataValue(
        x: string | number | null,
        series: string | null,
        metadataKey: string,
    ): string | undefined {
        const xId = typeof x === "number" ? `${x}` : x ?? undefined;
        const datum = this.getRawDatumForPoint(xId, series);
        const normalizedMetadataKey = this.normalizeMetadataKey(metadataKey);
        const value = datum?.metadata?.find((entry) =>
            this.normalizeMetadataKey(entry.name) === normalizedMetadataKey,
        )?.value;

        return typeof value === "string" && value.length ? value : undefined;
    }

    private normalizeMetadataKey(key: string): string {
        return key.replace(/\s+/g, "").toLowerCase();
    }

    /**
     * Data set used to compute scales and tooltips depending on render mode.
     * @returns Full dataset aligned with the active render mode.
     */
    @computed get chartDataForScale() {
        return this.currentRenderMode === RenderModeType.Percent
            ? this.fullPercentData
            : this.fullCountData;
    }

    /**
     * Formats raw chart data into Google Charts structures, handling percent conversions.
     * @param usePercent - Whether to normalize and display values as percentages.
     * @returns Formatted table data and series names, or null when no data exists.
     */
    private formatChartData(
        usePercent: boolean,
    ): {
        data: any[][];
        seriesNames: Set<string>;
        pointTooltips: Map<string, ReactElement>;
    } | null {
        if (!this.data) return null;

        // Links each chart point back to its original row, which still has the count
        const sourceByFormatted = new Map<SignalChartDatum, SignalReportDatum>();
        const pointTooltips = new Map<string, ReactElement>();

        const tooltipBuilder = (
            x: string | number | null,
            y: string | number | null,
            series: string | null,
            datum: SignalChartDatum | undefined,
            point: SignalChartPointIdentity,
        ) => {
            const shouldPreferSeriesInTooltip =
                this.config.renderOptions.preferSeriesInTooltip;

            const titleMetadataKey =
                this.config.renderOptions.tooltipTitleMetadataKey;
            const title = titleMetadataKey
                ? this.getPointMetadataValue(x, series, titleMetadataKey)
                : undefined;
            const sourceDatum = datum && sourceByFormatted.get(datum);

            const tooltip = dataPointTooltipBuilder({
                x,
                y,
                series: shouldPreferSeriesInTooltip ? series : null,
                isTemporal: !!this.config.renderOptions.isTemporal,
                tempGrain: this.temporalGrain,
                startDate: this.startDate!,
                endDate: this.endDate!,
                isPercent: usePercent,
                title,
                scoredConversationCount:
                    usePercent &&
                    this.config.renderOptions.showScoredConversationCount
                        ? getScoredConversationCount(sourceDatum)
                        : undefined,
                eligibleConversations: usePercent
                    ? this.getEligibleConversations(sourceDatum)
                    : undefined,
            });

            pointTooltips.set(getSignalChartPointKey(point), tooltip);
            return tooltip;
        };

        // Some endpoints return all series even when selections are set; filter client-side
        // so charts only render selected items.
        const renderableData = this.filterDataBySelectedIds(this.data);
        let groupingIdentifierToLegendName: Map<string, string> | undefined;
        if (
            this.dataEndpoint ===
            SignalsEndpoints.trends.AverageScoreByModuleOverTimeID
        ) {
            const canSelectAll = this.config.capabilities.canSelectAllModules;
            const scoredModules = this.optionsStore.orgScoredModules;
            const allModules = this.optionsStore.orgModules;
            const modules = canSelectAll
                ? allModules
                : scoredModules?.length
                    ? scoredModules
                    : allModules;

            const moduleLegendNameMap = new Map<string, string>();
            modules.forEach((module) => {
                const legendName = module.displayName ?? module.name;
                if (!module.id || !legendName) return;
                moduleLegendNameMap.set(`${module.id}`, legendName);
            });

            if (moduleLegendNameMap.size > 0) {
                groupingIdentifierToLegendName = moduleLegendNameMap;
            }
        }

        // Only normalize non-stacked topic charts here; stacked charts usually trust backend percentages.
        const isStacked =
            this.config.renderOptions.forceStacked || this.groupingStore.isGrouped;

        const percentReadyData =
            usePercent && !isStacked && this.selectionType === "Topics"
                ? renderableData.map((item) => ({
                      ...item,
                      y_percent:
                          item.isSyntheticTemporalGap || item.y_percent == null
                              ? undefined
                              : (item.y ?? 0) /
                                (this.optionsStore.totalConversationsValue ??
                                    1),
                      y_totalcount:
                          this.optionsStore.totalConversationsValue ??
                          undefined,
                  }))
                : renderableData;

        const sourceDataForFormat = usePercent
            ? this.addPercentRemainderSeries(percentReadyData)
            : renderableData;

        const dataToFormat = sourceDataForFormat.map((item) => {
            const formattedItem: SignalChartDatum = {
                ...item,
                y: item.isSyntheticTemporalGap
                    ? null
                    : usePercent
                      ? item.y_percent == null
                          ? null
                          : item.y_percent * 100
                      : item.y,
            };
            sourceByFormatted.set(formattedItem, item);
            return formattedItem;
        });
        const swapGroupingAndX = this.groupingStore.swapGroupingAndX;
        const formatted = signalDataToGoogleFormat({
            data: dataToFormat,
            showPercent: usePercent,
            tooltipBuilder,
            groupingIdentifierToLegendName,
            isComparePrev: this.comparePrevEnabled,
            isTemporal: this.config.renderOptions.isTemporal,
            temporalGrain: this.temporalGrain,
            swapGroupingAndX,
            filterMissingGroupedSeries: this.groupingStore.isGrouped,
            // Only when the date buckets are the x-axis. Grouped into the series they are column
            // headers, which are never date-parsed to begin with.
            preformattedXValues:
                this.groupingStore.activeGrouping?.kind === "date" &&
                swapGroupingAndX,
            useLabelsAsGroupingIds:
                this?.dataEndpoint ===
                SignalsEndpoints.additionalReports.ModuleResultsCountId,
        });

        // Volume ordering must always reflect underlying counts, not the
        // percent-transformed y values in dataToFormat. When percentages all
        // round to ~100 (e.g. stacked single-series), percent-based totals
        // collapse to a tie and produce an order that disagrees with count mode.
        const groupedBucketOrdered = this.sortFormattedGroupedBuckets(
            formatted,
            renderableData,
            swapGroupingAndX,
        );
        const groupedSeriesOrdered = this.sortFormattedGroupedSeries(
            groupedBucketOrdered,
        );

        if (!this.hasRenderableData(groupedSeriesOrdered)) return null;
        return { ...groupedSeriesOrdered, pointTooltips };
    }

    /** The count and total for a point's tooltip, or undefined if we shouldn't show them. */
    private getEligibleConversations(
        datum: SignalReportDatum | undefined,
    ): { count: number; total: number } | undefined {
        if (this.config.renderOptions.hideEligibleConversations) return undefined;

        return getEligibleConversations(
            datum,
            this.config.renderOptions.eligibleConversationsTotalField,
        );
    }

    private addPercentRemainderSeries(
        data: SignalReportDatum[],
    ): SignalReportDatum[] {
        if (!this.config.percentRemainderSeries || data.length === 0) {
            return data;
        }

        const buckets = new Map<
            string,
            { sample: SignalReportDatum; percentTotal: number; countTotal: number }
        >();

        data.forEach((datum) => {
            const bucketId = `${datum.x_identifier ?? datum.x}`;
            const bucket = buckets.get(bucketId) ?? {
                sample: datum,
                percentTotal: 0,
                countTotal: 0,
            };
            if (
                typeof datum.y_percent === "number" &&
                Number.isFinite(datum.y_percent)
            ) {
                bucket.percentTotal += datum.y_percent;
            }
            bucket.countTotal += datum.y ?? 0;
            buckets.set(bucketId, bucket);
        });

        const remainderData = Array.from(buckets.values()).flatMap(
            ({ sample, percentTotal, countTotal }) => {
                const remainder = Math.max(0, 1 - percentTotal);
                if (remainder <= PERCENT_REMAINDER_EPSILON) {
                    return [];
                }

                return [
                    {
                        x: sample.x,
                        x_identifier: sample.x_identifier,
                        // Conversations not in any of the shown bars
                        y:
                            sample.y_totalcount != null
                                ? Math.max(0, sample.y_totalcount - countTotal)
                                : 0,
                        y_totalcount: sample.y_totalcount,
                        y_percent: remainder,
                        grouping: PERCENT_REMAINDER_SERIES_LABEL,
                        grouping_identifier:
                            PERCENT_REMAINDER_SERIES_IDENTIFIER,
                    } as SignalReportDatum,
                ];
            },
        );

        return [...data, ...remainderData];
    }

    private sortFormattedGroupedSeries(
        formatted: { data: any[][]; seriesNames: Set<string> },
    ): { data: any[][]; seriesNames: Set<string> } {
        // Interactive grouping and explicitly opted-in force-stacked charts
        // resolve slices through the same post-format ordering stage. Grouped
        // charts use the grouping catalog rule when not swapped and the chart
        // rule when swapped. A volume slice rule is replaced with alphabetical
        // so ordering is stable globally.
        const sliceOrderRule =
            this.orderRulesStore.getFormattedSeriesOrderRule();

        if (
            (!this.groupingStore.isGrouped &&
                !this.orderRulesStore.usesFixedGroupingOrderRule()) ||
            !sliceOrderRule ||
            formatted.data.length <= 1
        ) {
            return formatted;
        }

        const [headerRow, ...rows] = formatted.data;
        if (!headerRow || headerRow.length <= 2) {
            return formatted;
        }

        const seriesColumnGroups = this.getFormattedSeriesColumnGroups(headerRow);
        if (seriesColumnGroups.length <= 1) {
            return formatted;
        }

        const sortedColumnGroups = [...seriesColumnGroups].sort((left, right) => {
            if (left.id === PERCENT_REMAINDER_SERIES_IDENTIFIER) return 1;
            if (right.id === PERCENT_REMAINDER_SERIES_IDENTIFIER) return -1;
            return this.orderRulesStore.compareBucketsByOrderRule(
                toOrderableBucket(left.label, left.id),
                toOrderableBucket(right.label, right.id),
                sliceOrderRule,
            );
        });

        const orderedColumnIndexes = [
            0,
            ...sortedColumnGroups.flatMap((group) => group.columnIndexes),
        ];

        return {
            ...formatted,
            data: [headerRow, ...rows].map((row) =>
                orderedColumnIndexes.map((columnIndex) => row[columnIndex]),
            ),
            seriesNames: new Set(sortedColumnGroups.map((group) => group.label)),
        };
    }

    private getFormattedSeriesColumnGroups(
        headerRow: any[],
    ): SummarySeriesColumn[] {
        const groups: SummarySeriesColumn[] = [];

        for (let index = 1; index < headerRow.length; index++) {
            const header = headerRow[index];
            if (header?.role === "tooltip") {
                continue;
            }

            const columnIndexes = [index];
            if (headerRow[index + 1]?.role === "tooltip") {
                columnIndexes.push(index + 1);
                index++;
            }

            const label =
                typeof header === "object"
                    ? `${header.label ?? header.id ?? ""}`
                    : `${header ?? ""}`;
            const id =
                typeof header === "object"
                    ? this.getFirstSummaryDataId(
                          header.id,
                          header.label,
                          header.v,
                          header.f,
                      )
                    : this.normalizeSummaryDataId(header);

            groups.push({
                id,
                label,
                columnIndexes,
            });
        }

        return groups;
    }

    private sortFormattedGroupedBuckets(
        formatted: { data: any[][]; seriesNames: Set<string> },
        sourceData: SignalReportDatum[],
        swapGroupingAndX: boolean,
    ): { data: any[][]; seriesNames: Set<string> } {
        // Line charts only need slice (series) ordering; the bucket axis is
        // temporal and is sorted by the formatter using the time grain.
        if (
            !this.groupingStore.isGrouped ||
            formatted.data.length <= 2 ||
            this.chartType === "Line"
        ) {
            return formatted;
        }

        const bucketOrderRule =
            this.orderRulesStore.getFormattedBucketOrderRule(swapGroupingAndX);
        if (!bucketOrderRule) {
            return formatted;
        }

        const [headerRow, ...rows] = formatted.data;
        const bucketTotals = new Map<string, number>();

        sourceData.forEach((datum) => {
            const bucketLabel = swapGroupingAndX
                ? `${datum.grouping ?? ""}`
                : `${datum.x ?? ""}`;
            bucketTotals.set(
                bucketLabel,
                (bucketTotals.get(bucketLabel) ?? 0) + Number(datum.y ?? 0),
            );
        });

        const sortedRows = [...rows].sort((left, right) =>
            this.compareGroupedBucketRows(left, right, bucketOrderRule, bucketTotals),
        );

        return {
            ...formatted,
            data: [headerRow, ...sortedRows],
        };
    }

    private compareGroupedBucketRows(
        left: any[],
        right: any[],
        orderRule: SignalsModels.OrderRule,
        bucketTotals: Map<string, number>,
    ): number {
        return this.orderRulesStore.compareBucketsByOrderRule(
            this.getFormattedBucket(left),
            this.getFormattedBucket(right),
            orderRule,
            bucketTotals,
        );
    }

    /**
     * A formatted row's x cell as something orderable: the text on the axis, plus the identifier
     * underneath it.
     *
     * Google's `{ v, f }` cell keeps the two apart, which is what lets continuous buckets order by
     * an ISO date while the axis reads as a date span.
     */
    private getFormattedBucket(row: any[]): OrderableBucket {
        const xValue = row?.[0];

        if (!xValue || typeof xValue !== "object") {
            return { label: `${xValue ?? ""}` };
        }

        return toOrderableBucket(
            `${xValue.f ?? xValue.v ?? ""}`,
            xValue.v == null ? undefined : `${xValue.v}`,
        );
    }

    /**
     * Returns normalized classifier category id, treating "All Classifiers" as undefined.
     */
    private getSelectedClassifierCategoryId(): string | undefined {
        return normalizeClassifierCategoryId(
            this.selectedIds.classifierCategory as string | undefined,
        );
    }

    /**
     * Computes classifier ids currently in scope for data requests and client-side filtering.
     */
    private getClassifierSelectionScope(): {
        ids: string[];
        hasCategoryFilter: boolean;
    } {
        const classifierCategoryId = this.getSelectedClassifierCategoryId();
        const hasCategoryFilter = !!classifierCategoryId;
        const scopedClassifiers = getClassifierOptionsForCategory(
            this,
            classifierCategoryId,
        );
        const scopedIdSet = new Set(
            scopedClassifiers.map((classifier) => `${classifier.id}`),
        );

        const selectedClassifierIds = Array.isArray(this.selectedIds.classifiers)
            ? this.selectedIds.classifiers.map((id) => `${id}`)
            : this.selectedIds.classifiers
                ? [`${this.selectedIds.classifiers}`]
                : [];

        if (selectedClassifierIds.length) {
            const validSelectedIds = selectedClassifierIds.filter((id) =>
                scopedIdSet.has(id),
            );

            if (validSelectedIds.length) {
                return {
                    ids: validSelectedIds,
                    hasCategoryFilter,
                };
            }

            if (hasCategoryFilter) {
                return {
                    ids: scopedClassifiers.map((classifier) => `${classifier.id}`),
                    hasCategoryFilter: true,
                };
            }

            return {
                ids: [],
                hasCategoryFilter: false,
            };
        }

        if (!hasCategoryFilter) {
            return { ids: [], hasCategoryFilter: false };
        }

        return {
            ids: scopedClassifiers.map((classifier) => `${classifier.id}`),
            hasCategoryFilter: true,
        };
    }

    /**
     * Derives selected ids that should be used to filter chart series.
     * @returns Array of selected ids (stringified).
     */
    private getSelectedIdsForFiltering(): string[] {
        const normalize = (value?: string | string[]) =>
            Array.isArray(value)
                ? value.map((id) => `${id}`)
                : value
                    ? [`${value}`]
                    : [];

        switch (this.selectionType) {
            case "Topics":
                return normalize(this.selectedIds.topics);
            case "Themes":
                return normalize(this.selectedIds.themes);
            case "Classifiers":
                return this.getClassifierSelectionScope().ids;
            case "Modules":
                return normalize(this.selectedIds.modules);
            case "Questions":
                return normalize(this.selectedIds.questions);
            default:
                return [];
        }
    }

    /**
     * Derives selected ids that should be persisted to viz options as defaults.
     * @returns Array of selected ids (stringified).
     */
    protected override getSelectedIdsForVizDefaults(): string[] {
        const normalize = (value?: string | string[]) =>
            Array.isArray(value)
                ? value.map((id) => `${id}`)
                : value
                    ? [`${value}`]
                    : [];

        switch (this.selectionType) {
            case "LLM Classifier":
            case "LLM Classifier Results":
                return normalize(this.selectedIds.llmClassifier);
            case "Module":
                return normalize(this.selectedIds.module);
            case "Question":
                return normalize(this.selectedIds.question);
            case "Tags":
                return normalize(this.selectedIds.tags);
            case "Topics":
                return normalize(this.selectedIds.topics);
            case "Themes":
                return normalize(this.selectedIds.themes);
            case "Classifiers":
                return normalize(this.selectedIds.classifiers);
            case "Modules":
                return normalize(this.selectedIds.modules);
            case "Questions":
                return normalize(this.selectedIds.questions);
            case "Classifier Group":
                return normalize(this.selectedIds.classifierGroup);
            default:
                return [];
        }
    }

    /**
     * Maps selected ids to series labels when possible (topics/classifiers).
     * @returns Array of selected labels (stringified).
     */
    private getSelectedLabelsForFiltering(): string[] {
        const selectedIds = new Set(this.getSelectedIdsForFiltering());
        if (!selectedIds.size) return [];

        const byId = <T>(
            items: T[],
            idSelector: (item: T) => string,
            labelSelector: (item: T) => string,
        ) =>
            items
                .filter((item) => selectedIds.has(idSelector(item)))
                .map(labelSelector)
                .filter((label) => label.length > 0);

        switch (this.selectionType) {
            case "Topics":
                return byId(
                    this.topics ?? [],
                    (topic) => `${topic.id}`,
                    (topic) => `${topic.topicLabel}`,
                );
            case "Themes":
                return byId(
                    this.themes ?? [],
                    (theme) => `${theme.id}`,
                    (theme) => `${theme.themeLabel}`,
                );
            case "Classifiers":
                return byId(
                    this.optionsStore.userClassifiers ?? [],
                    (classifier) => `${classifier.id}`,
                    (classifier) => `${classifier.name}`,
                );
            case "Modules": {
                const canSelectAll =
                    this.config.capabilities.canSelectAllModules;
                const scoredModules = this.optionsStore.orgScoredModules ?? [];
                const allModules = this.optionsStore.orgModules ?? [];
                const modules = canSelectAll
                    ? allModules
                    : scoredModules.length
                        ? scoredModules
                        : allModules;
                return byId(
                    modules,
                    (module) => `${module.id}`,
                    (module) => `${module.displayName ?? module.name ?? ""}`,
                );
            }
            case "Questions": {
                const module =
                    this.optionsStore.orgModules.find(
                        (m) => m.id === this.selectedIds.module,
                    ) ?? this.optionsStore.orgModules[0];
                const questions = module?.questions ?? [];
                return byId(
                    questions,
                    (question) => `${question.id ?? ""}`,
                    (question) =>
                        `${question.shortQuestionText ?? question.questionText ?? ""}`,
                );
            }
            default:
                return [];
        }
    }

    /**
     * Filters chart data based on selected ids/labels when available.
     * @param data - Raw chart data rows.
     * @returns Filtered chart data rows.
     */
    private filterDataBySelectedIds(
        data: SignalReportDatum[],
    ): SignalReportDatum[] {
        if (this.config.dataGrid?.selection) {
            if (
                this.selectionType === "Modules" &&
                Array.isArray(this.selectedIds.modules) &&
                this.selectedIds.modules.length === 0
            ) {
                return [];
            }
            if (
                this.selectionType === "Questions" &&
                Array.isArray(this.selectedIds.questions) &&
                this.selectedIds.questions.length === 0
            ) {
                return [];
            }
            if (
                this.selectionType === "Tags" &&
                Array.isArray(this.selectedIds.tags) &&
                this.selectedIds.tags.length === 0
            ) {
                return [];
            }
        }

        const classifierScope =
            this.selectionType === "Classifiers"
                ? this.getClassifierSelectionScope()
                : { ids: [], hasCategoryFilter: false };

        const selectedIds =
            this.selectionType === "Classifiers"
                ? classifierScope.ids
                : this.getSelectedIdsForFiltering();

        if (!selectedIds.length) {
            if (
                this.selectionType === "Classifiers" &&
                classifierScope.hasCategoryFilter
            ) {
                return [];
            }
            return data;
        }

        const selectedIdSet = new Set(selectedIds);
        const selectedLabelSet = new Set(this.getSelectedLabelsForFiltering());

        const toKey = (value?: string | number | null) =>
            value === undefined || value === null ? undefined : `${value}`;

        // Match against both ids and labels; some bar charts put the selection on x/x_identifier,
        // while others use grouping/grouping_identifier.
        return data.filter((datum) => {
            const groupingId = toKey(datum.grouping_identifier);
            const groupingLabel = toKey(datum.grouping);
            const xId = toKey(datum.x_identifier);
            const xLabel = toKey(datum.x);

            if (groupingId && selectedIdSet.has(groupingId)) {
                return true;
            }
            if (xId && selectedIdSet.has(xId)) {
                return true;
            }
            if (groupingLabel && selectedLabelSet.has(groupingLabel)) {
                return true;
            }
            if (xLabel && selectedLabelSet.has(xLabel)) {
                return true;
            }
            if (xLabel && selectedIdSet.has(xLabel)) {
                return true;
            }
            return false;
        });
    }

    /**
     * Returns the identifier used to map chart rows to paginated summary context.
     * Matches the rendered pagination axis: rows for bar charts, series columns
     * for line charts, and the flipped dimension when grouping swaps x/grouping.
     */
    private getSummaryDataId(
        datum: SignalReportDatum,
        axis: SummaryPaginationAxis,
    ): string | undefined {
        const shouldUseGrouping =
            axis === "xAxis"
                ? this.groupingStore.swapGroupingAndX
                : !this.groupingStore.swapGroupingAndX;
        const id = shouldUseGrouping
            ? datum.grouping_identifier ?? datum.grouping
            : datum.x_identifier ?? datum.x;

        return this.normalizeSummaryDataId(id);
    }

    private normalizeSummaryDataId(id: unknown): string | undefined {
        if (id === undefined || id === null) return undefined;
        const normalized = `${id}`;
        return normalized.length ? normalized : undefined;
    }

    private getFirstSummaryDataId(...ids: unknown[]): string | undefined {
        for (const id of ids) {
            const normalized = this.normalizeSummaryDataId(id);
            if (normalized) return normalized;
        }

        return undefined;
    }

    private getSummaryPaginationAxis(): SummaryPaginationAxis {
        return this.chartType === "Line" ? "series" : "xAxis";
    }

    private getFormattedXAxisId(row: any[]): string | undefined {
        return this.getFormattedXAxisDescriptor(row)?.id;
    }

    private getFormattedXAxisDescriptor(
        row: any[],
    ): SummaryDatumDescriptor | undefined {
        const xValue = row?.[0];

        if (xValue && typeof xValue === "object") {
            const label = this.getSummaryLabel(
                this.getFirstSummaryDataId(xValue.f, xValue.label, xValue.v),
            );
            const id = this.getFirstSummaryDataId(
                xValue.v,
                xValue.id,
                xValue.f,
                xValue.label,
            );

            return label ? { id, label } : undefined;
        }

        const label = this.getSummaryLabel(xValue);
        if (!label) return undefined;

        return {
            id: this.normalizeSummaryDataId(xValue),
            label,
        };
    }

    private getSummaryLabel(value: unknown): string | undefined {
        if (value instanceof Date) {
            return this.formatSummaryDateLabel(value);
        }

        return this.normalizeSummaryDataId(value);
    }

    private formatSummaryDateLabel(value: Date): string {
        switch (this.temporalGrain) {
            case "Quarterly":
                return moment(value).format("[Q]Q YYYY");
            case "Monthly":
                return moment(value).format("MMM YYYY");
            default:
                return moment(value).format("MMM DD, YYYY");
        }
    }

    private getVisibleGroupedSummaryContext(): {
        dataIdsInView: string[];
        rowsInView: SignalReportDatum[];
    } {
        const countData = this.countData;
        if (!countData?.length) {
            return { dataIdsInView: [], rowsInView: [] };
        }

        const percentData = this.percentData;
        const headerRow = countData[0];
        const seriesColumns = this.getFormattedSeriesColumnGroups(headerRow);
        if (!seriesColumns.length) {
            return { dataIdsInView: [], rowsInView: [] };
        }

        const dataIdsInView: string[] = [];
        const rowsInView: SignalReportDatum[] = [];

        countData.slice(1).forEach((row, rowOffset) => {
            const xAxis = this.getFormattedXAxisDescriptor(row);
            if (!xAxis) return;

            const percentRow = percentData?.[rowOffset + 1];

            seriesColumns.forEach((series) => {
                const yValue = row[series.columnIndexes[0]];
                if (yValue === null || yValue === undefined) return;

                const y = Number(yValue);
                if (!Number.isFinite(y)) return;

                const dataId = this.getSummaryCellDataId(xAxis, series);
                dataIdsInView.push(dataId);

                rowsInView.push(
                    this.getRawSummaryDatumForVisibleCell(xAxis, series) ??
                        this.buildSummaryDatumFromVisibleCell(
                            xAxis,
                            series,
                            y,
                            percentRow?.[series.columnIndexes[0]],
                        ),
                );
            });
        });

        return { dataIdsInView, rowsInView };
    }

    private getSummaryCellDataId(
        xAxis: SummaryDatumDescriptor,
        series: SummaryDatumDescriptor,
    ): string {
        return `${xAxis.id ?? xAxis.label}::${series.id ?? series.label}`;
    }

    private getRawSummaryDatumForVisibleCell(
        xAxis: SummaryDatumDescriptor,
        series: SummaryDatumDescriptor,
    ): SignalReportDatum | undefined {
        const candidateData = this.filterDataBySelectedIds(this.data ?? []);

        return candidateData.find((datum) =>
            this.doesDatumMatchVisibleCell(datum, xAxis, series),
        );
    }

    private doesDatumMatchVisibleCell(
        datum: SignalReportDatum,
        xAxis: SummaryDatumDescriptor,
        series: SummaryDatumDescriptor,
    ): boolean {
        const swapGroupingAndX = this.groupingStore.swapGroupingAndX;
        const datumXAxisId = swapGroupingAndX
            ? datum.grouping_identifier
            : datum.x_identifier;
        const datumXAxisLabel = swapGroupingAndX ? datum.grouping : datum.x;
        const datumSeriesId = swapGroupingAndX
            ? datum.x_identifier
            : datum.grouping_identifier;
        const datumSeriesLabel = swapGroupingAndX ? datum.x : datum.grouping;

        return (
            this.doesSummaryDescriptorMatch(datumXAxisId, datumXAxisLabel, xAxis) &&
            this.doesSummaryDescriptorMatch(
                datumSeriesId,
                datumSeriesLabel,
                series,
            )
        );
    }

    private doesSummaryDescriptorMatch(
        datumId: unknown,
        datumLabel: unknown,
        descriptor: SummaryDatumDescriptor,
    ): boolean {
        const candidates = [
            this.normalizeSummaryDataId(datumId),
            this.getSummaryLabel(datumLabel),
        ].filter((value): value is string => !!value);

        return candidates.some(
            (candidate) =>
                candidate === descriptor.id || candidate === descriptor.label,
        );
    }

    private buildSummaryDatumFromVisibleCell(
        xAxis: SummaryDatumDescriptor,
        series: SummaryDatumDescriptor,
        y: number,
        percentValue: unknown,
    ): SignalReportDatum {
        const hasPercentValue =
            percentValue !== null && percentValue !== undefined;
        const yPercent = hasPercentValue ? Number(percentValue) : NaN;
        const swapGroupingAndX = this.groupingStore.swapGroupingAndX;
        const datum = new SignalReportDatum();

        datum.x = swapGroupingAndX ? series.label : xAxis.label;
        datum.x_identifier = swapGroupingAndX ? series.id : xAxis.id;
        datum.grouping = swapGroupingAndX ? xAxis.label : series.label;
        datum.grouping_identifier = swapGroupingAndX ? xAxis.id : series.id;
        datum.y = y;
        if (Number.isFinite(yPercent)) {
            datum.y_percent = yPercent / 100;
        }

        return datum;
    }

    private getFormattedSummaryDataIds(
        axis: SummaryPaginationAxis,
    ): string[] {
        const formattedCount = this.formattedCountData;
        if (!formattedCount) return [];

        if (axis === "series") {
            const headerRow = formattedCount.data[0];
            if (!headerRow) return [];

            return this.getFormattedSeriesColumnGroups(headerRow)
                .map((group) =>
                    this.normalizeSummaryDataId(group.id ?? group.label),
                )
                .filter((id): id is string => !!id);
        }

        return formattedCount.data
            .slice(1)
            .map((row) => this.getFormattedXAxisId(row))
            .filter((id): id is string => !!id);
    }

    /**
     * Builds a stable map of summary data ids to underlying raw rows.
     */
    private buildSummaryRowsByDataId(
        data: SignalReportDatum[],
        axis: SummaryPaginationAxis,
    ): Map<string, SignalReportDatum[]> {
        const rowsByDataId = new Map<string, SignalReportDatum[]>();

        for (const datum of data) {
            const dataId = this.getSummaryDataId(datum, axis);
            if (!dataId) continue;

            const currentRows = rowsByDataId.get(dataId);
            if (currentRows) {
                currentRows.push(datum);
                continue;
            }
            rowsByDataId.set(dataId, [datum]);
        }

        return rowsByDataId;
    }

    /**
     * Computes the current paginated id set and corresponding rows for AI summaries.
     */
    private getPaginatedSummaryContext(): {
        dataIdsInView: string[];
        rowsInView: SignalReportDatum[];
    } {
        if (this.groupingStore.isGrouped) {
            const visibleGroupedContext = this.getVisibleGroupedSummaryContext();
            if (
                visibleGroupedContext.dataIdsInView.length > 0 ||
                visibleGroupedContext.rowsInView.length > 0
            ) {
                return visibleGroupedContext;
            }
        }

        const axis = this.getSummaryPaginationAxis();
        const filteredData = this.filterDataBySelectedIds(this.data ?? []);
        const rowsByDataId = this.buildSummaryRowsByDataId(filteredData, axis);
        const formattedDataIds = this.getFormattedSummaryDataIds(axis);
        const allDataIds =
            formattedDataIds.length > 0
                ? formattedDataIds
                : Array.from(rowsByDataId.keys());

        const page = Math.min(this.currentPage, this.maxPage);
        const pageStart = page * this.pageSize;
        const pageEnd = pageStart + this.pageSize;

        const dataIdsInView =
            this.isPaginated && this.pageSize > 0
                ? allDataIds.slice(pageStart, pageEnd)
                : allDataIds;

        const rowsInView: SignalReportDatum[] = [];
        for (const dataId of dataIdsInView) {
            rowsInView.push(...(rowsByDataId.get(dataId) ?? []));
        }

        return { dataIdsInView, rowsInView };
    }

    /**
     * Syncs AI summary staleness against the currently visible paginated data ids.
     */
    private syncAISummaryStateForCurrentPage() {
        if (!this.aiSummaryStore || !this.isSummaryPaginated) return;

        const { dataIdsInView } = this.getPaginatedSummaryContext();
        this.dataIdsInView = [...dataIdsInView];

        const sortedIdsUsedForSummary = [
            ...(this.dataIdsUsedForSummary ?? []),
        ].sort();
        const sortedIdsInView = [...this.dataIdsInView].sort();

        const isSummaryStillCurrent = sortedIdsUsedForSummary.every(
            (value, index) => value === sortedIdsInView[index],
        );

        if (isSummaryStillCurrent || !this.dataIdsUsedForSummary) {
            this.aiSummaryStore.setIsSummaryOutdated(false);
            this.aiSummaryStore.setIsSummaryRefreshable(false);
            return;
        }

        this.aiSummaryStore.setIsSummaryOutdated(true);
        this.aiSummaryStore.setIsSummaryRefreshable(true);
    }

    // === Pagination calculations ===
    /**
     * Total items available for pagination based on formatted count data.
     * @returns Number of items available to paginate.
     */
    @computed get totalItems() {
        if (!this.isPaginated) return 0;

        const formattedCount = this.formattedCountData;
        if (!formattedCount) return 0;

        if (this.chartType === "Line") {
            return this.getLineSeriesCount(formattedCount.data);
        }

        return Math.max(formattedCount.data.length - 1, 0);
    }

    /**
     * Maximum page index given the total items and page size.
     * @returns Highest permissible page index.
     */
    @computed get maxPage() {
        if (!this.isPaginated || this.pageSize <= 0) return 0;

        return Math.max(Math.ceil(this.totalItems / this.pageSize) - 1, 0);
    }

    /**
     * Temporal grain derived from the current date range.
     * @returns Grain string such as Daily/Weekly/etc.
     */
    @computed get temporalGrain() {
        const startDate = this.startDate;
        const endDate = this.endDate;
        if (!startDate || !endDate) return "Daily"; // default

        return temporalGrain(startDate, endDate);
    }

    /**
     * Start date used for the currently rendered chart data.
     * Prefers the last request state so labels/tooltips do not shift until refresh.
     */
    @computed get startDate() {
        const requestStartDate = this.state?.startDate;
        if (requestStartDate) {
            const parsedStartDate = moment(requestStartDate);
            if (parsedStartDate.isValid()) {
                return parsedStartDate;
            }
        }
        return this.getActiveDatePickerStore().beginDate;
    }

    /**
     * End date used for the currently rendered chart data.
     * Prefers the last request state so labels/tooltips do not shift until refresh.
     */
    @computed get endDate() {
        const requestEndDate = this.state?.endDate;
        if (requestEndDate) {
            const parsedEndDate = moment(requestEndDate);
            if (parsedEndDate.isValid()) {
                return parsedEndDate;
            }
        }
        return this.getActiveDatePickerStore().endDate;
    }

    /**
     * Updates a selected id bucket and clears compare-previous cache.
     * @param key - Selection bucket key.
     * @param value - New selection value(s).
     * @returns void
     */
    @action
    setSelectedId(key: SelectedIdKey, value?: string | string[]) {
        this.selectedIds = { ...this.selectedIds, [key]: value };
        this.comparePrevData = null;
    }

    /**
     * True when selection changes can be applied locally without a chart request.
     */
    @computed
    private get supportsClientSideSelectionFiltering(): boolean {
        if (this.config.dataGrid?.selection?.behavior === "noRequest") {
            return true;
        }

        if (this.config.renderOptions.cacheKeyStrategy !== "filtersChange") {
            return false;
        }

        return (
            this.selectionType === "Topics" ||
            this.selectionType === "Classifiers" ||
            this.selectionType === "Modules" ||
            this.selectionType === "Questions"
        );
    }

    /**
     * Handles selection updates from chart controls.
     * Runs a server refresh only when selection materially changes request data.
     */
    public handleSelectionChange(options?: {
        skipDataGridRefresh?: boolean;
        forceRequest?: boolean;
    }) {
        if (options?.forceRequest || !this.supportsClientSideSelectionFiltering) {
            void this.runRefresh(options?.forceRequest ?? false, options);
            return;
        }

        this.syncDataGridRowsForCurrentSelection();
        if (this.aiSummaryStore) {
            if (this.isSummaryPaginated) {
                this.syncAISummaryStateForCurrentPage();
            } else {
                this.aiSummaryStore.setIsSummaryOutdated(true);
                this.aiSummaryStore.setIsSummaryRefreshable(true);
            }
        }
    }

    /**
     * Updates visible series derived from data grid selections.
     * @param series - Series labels to display.
     */
    @action
    setVisibleSeries(series?: string[]) {
        this.gridVisibleSeries = series;
    }

    /**
     * Applies a data grid selection, queuing if the grid is not yet ready.
     * @param selectionIds - Row ids to select.
     */
    public applyDataGridSelection(selectionIds?: Array<string | number>) {
        const selectionConfig = this.config.dataGrid?.selection;
        if (!selectionConfig) return;
        const normalized = selectionIds ?? [];
        if (!this.dataGridStore || !this.dataGridSetupComplete) {
            this.pendingGridSelection = normalized;
            return;
        }
        this.pendingGridSelection = undefined;
        const rowSelectionIds =
            this.resolveSelectionIdsAgainstDataGridRows(normalized);

        this.dataGridStore.removeAllSelectedRows();
        if (selectionConfig.behavior === "request") {
            this.dataGridStore.changeSelectedRowIds(rowSelectionIds);
            const nextVisibleSeries = selectionConfig.onSelect({
                chartStore: this,
                dataGridStore: this.dataGridStore,
            });
            this.setVisibleSeries(nextVisibleSeries);
            return;
        }

        void this.dataGridStore.handleSelectionChange(rowSelectionIds);
    }

    private resolveSelectionIdsAgainstDataGridRows(
        selectionIds: Array<string | number>,
    ): Array<string | number> {
        if (!this.dataGridStore || selectionIds.length === 0) {
            return selectionIds;
        }

        const rows =
            (this.dataGridStore.allRows?.length ?? 0) > 0
                ? this.dataGridStore.allRows
                : this.dataGridStore.rows;
        const rowIdMap = new Map<string, string | number>();

        rows.forEach((row) => {
            const rowId = (row as GridRowModel).id;
            if (rowId !== undefined && rowId !== null) {
                rowIdMap.set(`${rowId}`, rowId as string | number);
            }
        });

        return selectionIds.map((id) => rowIdMap.get(`${id}`) ?? id);
    }

    private shouldReplayDataGridSelection(): boolean {
        if (!this.config.dataGrid?.selection || !this.dataGridStore) {
            return false;
        }

        const rows =
            (this.dataGridStore.allRows?.length ?? 0) > 0
                ? this.dataGridStore.allRows
                : this.dataGridStore.rows;
        if ((rows?.length ?? 0) === 0) {
            return false;
        }

        const selectedIds = this.dataGridStore.selectedRowIds.map(
            (id) => `${id}`,
        );
        const rowIdSet = new Set(
            rows
                .map((row) => (row as GridRowModel).id)
                .filter((id) => id !== undefined && id !== null)
                .map((id) => `${id}`),
        );
        const hasAnyMatchingRow = selectedIds.some((id) => rowIdSet.has(id));
        if (!hasAnyMatchingRow) {
            return false;
        }

        return (
            this.dataGridStore.selectedRowIds.length > 0 &&
            this.dataGridStore.selectedRows.length === 0
        );
    }

    /**
     * Recomputes visible grid rows for the current local selection state.
     */
    @action
    private syncDataGridRowsForCurrentSelection() {
        if (!this.dataGridStore) return;
        this.dataGridStore.setRows(
            this.getPaginatedDataGridRows(this.dataGridStore.allRows ?? []),
        );
    }

    /**
     * Derives the Themes a Card on the legacy Fast Reporting Theme path is currently showing,
     * read back off its rendered data, for use in its selector and drilldowns.
     *
     * The Primary Driver equivalent is {@link themes}. The two are deliberately separate: a Card
     * reads exactly one Theme contract, and letting either selector or drilldown source its
     * categories from the other would silently cross the two.
     *
     * @returns Array of SignalsTopics sorted alphabetically.
     */
    @computed
    get topics() {
        if (!this.data) return [];
        let returnValue: SignalsModels.SignalsTopics[] = [];
        if (
            this.dataEndpoint ===
            SignalsEndpoints.legacyTopicReports.PrimaryThemesId
        ) {
            const topicsById = new Map<string, SignalsModels.SignalsTopics>();

            this.data.forEach((datum) => {
                const id = datum.x_identifier ?? datum.x ?? "";
                if (!id.length || topicsById.has(id)) return;

                topicsById.set(id, {
                    id,
                    topicLabel: datum.x,
                });
            });

            returnValue = Array.from(topicsById.values());
        } else if (
            this.dataEndpoint ===
            SignalsEndpoints.legacyTopicReports.ThemeAndEEID
        ) {
            const ids = this.data.map((i) => i.x_identifier ?? "");
            const uniqueIds = Array.from(new Set(ids));
            returnValue = uniqueIds.map((id) => {
                return {
                    id,
                    topicLabel:
                        this.data!.find((i) => i.x_identifier === id)?.x ?? "",
                };
            });
        } else if (
            this.dataEndpoint ===
            SignalsEndpoints.legacyTopicReports.ThemesOverTimeId
        ) {
            const labels = this.data.map((datum) => datum.grouping);

            const uniqueLabels = Array.from(new Set(labels));

            returnValue = uniqueLabels.map((i) => {
                return {
                    topicLabel: i ?? "",
                    id: this.data!.find((d) => d.grouping === i)
                        ?.grouping_identifier ?? "",
                };
            });
        }

        returnValue = [
            ...returnValue.sort((a, b) => {
                return a.topicLabel.localeCompare(b.topicLabel);
            }),
        ];
        return returnValue;
    }

    /**
     * Derives the Themes a Card on the Primary Driver path is currently showing, read back off
     * its rendered data, for use in its selector and drilldowns.
     *
     * The legacy Fast Reporting equivalent is {@link topics}. The datum shapes happen to match,
     * but the two stay separate on purpose: these labels are Driver Themes and belong in the
     * `themes` filter, and reading them through a Topic-named accessor is exactly how the two
     * Theme contracts would quietly get crossed.
     *
     * @returns Array of SignalsThemes sorted alphabetically.
     */
    @computed
    get themes() {
        if (!this.data) return [];
        let returnValue: SignalsModels.SignalsThemes[] = [];

        if (
            this.dataEndpoint === SignalsEndpoints.themes.ThemePrevalenceId ||
            this.dataEndpoint === SignalsEndpoints.themes.ThemePrevalenceAndEEId
        ) {
            // Prevalence and Eddy both put the Theme on the X axis.
            const themesById = new Map<string, SignalsModels.SignalsThemes>();

            this.data.forEach((datum) => {
                const id = datum.x_identifier ?? datum.x ?? "";
                if (!id.length || themesById.has(id)) return;

                themesById.set(id, {
                    id,
                    themeLabel: datum.x,
                });
            });

            returnValue = Array.from(themesById.values());
        } else if (
            this.dataEndpoint ===
            SignalsEndpoints.themes.ThemePrevalenceOverTimeId
        ) {
            // Over Time puts the Theme on the series instead, with the date on X.
            const themesById = new Map<string, SignalsModels.SignalsThemes>();

            this.data.forEach((datum) => {
                const id = datum.grouping_identifier ?? datum.grouping ?? "";
                if (!id.length || themesById.has(id)) return;

                themesById.set(id, {
                    id,
                    themeLabel: datum.grouping ?? "",
                });
            });

            returnValue = Array.from(themesById.values());
        }

        return [
            ...returnValue.sort((a, b) =>
                a.themeLabel.localeCompare(b.themeLabel),
            ),
        ];
    }

    // === User controls ===
    /**
     * Toggles compare-previous mode and fetches data as needed.
     * @returns Promise resolved after refresh completes.
     */
    @action async toggleComparePrevious() {
        const shouldEnable = !this.comparePrevEnabled;
        if (shouldEnable && this.groupingStore.isGrouped) {
            return;
        }

        this.comparePrevEnabled = shouldEnable;
        const filtersSignature =
            this.lastFiltersSignature ?? this.getFiltersSignature();
        this.lastFiltersSignature = filtersSignature;
        const selectionSignature = this.getSelectionsSignature();
        const comparePrevCacheKey = this.buildComparePrevCacheKey(
            filtersSignature,
            selectionSignature,
        );

        if (
            shouldEnable &&
            this.config.capabilities.comparePrev &&
            (!this.comparePrevData ||
                this.comparePrevCacheKey !== comparePrevCacheKey)
        ) {
            await this.getComparePrevData(filtersSignature);
        }
        if (!shouldEnable) {
            this.comparePrevData = null;
            this.comparePrevCacheKey = null;
        }

        await this.runRefresh();
        // save to vizoptions?
    }

    /**
     * Toggles between count and percent render modes.
     * @returns void
     */
    @action toggleRenderMode() {
        this.currentRenderMode =
            this.currentRenderMode === RenderModeType.Count
                ? RenderModeType.Percent
                : RenderModeType.Count;
        // Keep the y-axis label in sync with the active render mode
        this.yTitle =
            this.currentRenderMode === RenderModeType.Percent
                ? "Percent"
                : "Count";
        // Save to vizoptions?
    }

    /**
     * Sets the current page index for paginated charts.
     * @param page - New zero-based page index.
     * @returns void
     */
    @action
    setPage(page: number) {
        if (page === this.currentPage) return;
        this.currentPage = page;
        this.syncAISummaryStateForCurrentPage();
    }

    /**
     * Advances pagination forward by one page.
     * @returns void
     */
    @action
    nextPage() {
        if (!this.isPaginated) return;
        this.setPage(this.currentPage + 1);
    }

    /**
     * Moves pagination backward by one page.
     * @returns void
     */
    @action
    previousPage() {
        if (!this.isPaginated) return;
        this.setPage(this.currentPage - 1);
    }

    // === Pagination helpers ===
    /**
     * Applies pagination to Google Chart data and syncs the grid when needed.
     * @param data - Google Chart data table to paginate.
     * @returns Paginated data table respecting current page and page size.
     */
    applyPagination(data: any[][]): any[][] {
        if (!this.pageSize || !this.isPaginated) return data;

        const page = Math.min(this.currentPage, this.maxPage);

        if (
            this.config.dataGrid &&
            this.dataGridStore &&
            this.config.dataGrid.followPagination
        ) {
            this.dataGridStore.setRows(
                this.getPaginatedDataGridRows(this.dataGridStore.allRows),
            );
        }

        if (this.chartType === "Line") {
            const header = data[0];
            if (!header) return data;

            const hasTooltipColumns = header
                .slice(1)
                .some((col) => (col as any)?.role === "tooltip");
            const columnsPerSeries = hasTooltipColumns ? 2 : 1;
            const seriesCount = Math.max(
                this.seriesNames.length,
                this.getLineSeriesCount(data),
            );

            if (seriesCount <= this.pageSize || columnsPerSeries <= 0) {
                return data;
            }

            const startSeries = page * this.pageSize;
            const endSeries = startSeries + this.pageSize;

            const keepColumns: number[] = [0]; // always keep the x-axis column
            for (
                let seriesIndex = startSeries;
                seriesIndex < endSeries;
                seriesIndex++
            ) {
                const columnStart = 1 + seriesIndex * columnsPerSeries;
                for (let offset = 0; offset < columnsPerSeries; offset++) {
                    const columnIndex = columnStart + offset;
                    if (columnIndex < header.length) {
                        keepColumns.push(columnIndex);
                    }
                }
            }

            return data.map((row) =>
                keepColumns.map((columnIndex) => row[columnIndex]),
            );
        }

        const header = data[0];
        const rest = data.slice(1);
        const startIndex = page * this.pageSize;
        const endIndex = startIndex + this.pageSize;
        const paginatedData = rest.slice(startIndex, endIndex);
        const result = [header, ...paginatedData];
        return result;
    }

    /**
     * Counts how many series exist in a Google Chart line dataset.
     * @param data - Google Chart data table.
     * @returns Number of series represented in the dataset.
     */
    private getLineSeriesCount(data: any[][] | null): number {
        if (!data || data.length === 0) return 0;
        const header = data[0];
        const nonXColumns = Math.max(header.length - 1, 0);

        const hasTooltipColumns = header
            .slice(1)
            .some((col) => (col as any)?.role === "tooltip");
        const columnsPerSeries = hasTooltipColumns ? 2 : 1;

        return Math.ceil(nonXColumns / columnsPerSeries);
    }

    // === AI summaries ===
    /**
     * Captures the current paginated id set as the source for the next AI summary.
     */
    @action
    captureAISummaryContextForCurrentPage() {
        if (!this.isSummaryPaginated) {
            this.dataIdsUsedForSummary = undefined;
            return;
        }

        const { dataIdsInView } = this.getPaginatedSummaryContext();
        this.dataIdsInView = [...dataIdsInView];
        this.dataIdsUsedForSummary = [...dataIdsInView];
    }

    /**
     * Refreshes the AI summary for the chart when supported.
     * @returns Promise resolved when summary refresh completes or is skipped.
     */
    @action
    async refreshAISummary() {
        if (!this.aiSummaryStore) return;
        const dataEndpoint = this.dataEndpoint;

        if (!dataEndpoint) return;

        if (this.isSummaryPaginated) {
            const { dataIdsInView, rowsInView } =
                this.getPaginatedSummaryContext();
            this.dataIdsInView = [...dataIdsInView];
            this.dataIdsUsedForSummary = [...dataIdsInView];
            return this.aiSummaryStore.refreshSummaryWithRowData(
                this.id,
                rowsInView,
                dataEndpoint,
            );
        } else {
            this.dataIdsUsedForSummary = undefined;
            return this.aiSummaryStore.refreshSummary(
                this.getChartState(),
                dataEndpoint,
            );
        }
    }

    // === Chart references ===
    /**
     * Captures a reference to the Google Chart wrapper for later interactions.
     * @param chartWrapper - Wrapper instance provided by react-google-charts.
     * @returns void
     */
    @action
    captureChartRef = (chartWrapper: GoogleChartWrapper) => {
        this.chartWrapper = chartWrapper;
    };

    // === CSV export ===
    /**
     * Returns exportable field nodes formatted for tree selection controls.
     * @returns Tree nodes representing available exportable fields.
     */
    @computed
    get exportableFieldNodes(): FieldNode[] {
        if (!this.exportableFields) return [];

        const filterItemsStore = this.rootStore.getStore(
            ApplicationFilterItemsStore,
        );

        return this.exportableFields.flatMap((group) => {
            if (!Array.isArray(group.fields)) return [];

            return {
                key: SignalsModels.getExportableFieldCategoryString(
                    group.category,
                ),
                label: SignalsModels.getExportableFieldCategoryString(
                    group.category,
                ),
                path: [],
                children: group.fields.map((field) => ({
                    key: field.name,
                    // Tree labels are plain strings and cannot carry a status tag, so the marker is what
                    // tells the two identically-labelled Theme columns apart -- for the viewers who can
                    // see that status. See EXPORT_FIELD_FILTER_KEYS.
                    label: filterItemsStore.withDeprecationMarker(
                        EXPORT_FIELD_FILTER_KEYS[field.name],
                        field.label,
                    ),
                    path: field.name.split("."),
                })),
            };
        });
    }

    /**
     * Indicates whether the user can select columns when exporting CSV.
     * @returns True when server-side export with column selection is enabled.
     */
    @computed
    get allowColumnSelection(): boolean {
        return (
            this.config.capabilities.csvExport === "server-export" &&
            !!this.config.exportConfig?.allowColumnSelection
        );
    }

    /**
     * Exports chart data as CSV using either server or client strategy.
     * @param selectedColumns - Optional columns to include for server export.
     * @returns Blob or server response containing CSV data, or null on failure.
     */
    public async exportCsv(selectedColumns?: string[]) {
        if (!this.dataEndpoint) return null;

        const strategy = this.config.capabilities.csvExport;
        if (strategy === "none") return null;

        try {
            if (strategy === "server-export") {
                const state = this.getChartState();
                return await this.signalsService.getChartExport(
                    this.dataEndpoint,
                    state,
                    selectedColumns,
                );
            }

            if (strategy === "client-csv") {
                return this.buildClientCsvBlob();
            }
        } catch (error) {
            this.rootStore
                .getStore(MessageStore)
                .logError("Failed to export chart data as CSV.");
            return null;
        }

        return null;
    }

    /**
     * Loads available exportable fields, using cached requests when possible.
     * @returns Promise resolved after exportable fields load or fail.
     */
    @action
    async loadExportableFields() {
        if (this.exportableFields) return;
        if (this.exportableFieldsLoading) return;
        if (!this.dataEndpoint) return;

        this.exportableFieldsLoading = true;
        try {
            const cacheKey = this.dataEndpoint;
            const cachedFields =
                ChartCardStore.exportableFieldsCache.get(cacheKey);
            if (cachedFields) {
                this.exportableFields = cachedFields;
                return;
            }

            let exportableFieldsRequest =
                ChartCardStore.exportableFieldsRequest.get(cacheKey);
            if (!exportableFieldsRequest) {
                exportableFieldsRequest =
                    this.signalsService.getChartExportableFields(cacheKey);
                ChartCardStore.exportableFieldsRequest.set(
                    cacheKey,
                    exportableFieldsRequest,
                );
            }

            const exportableFields = await exportableFieldsRequest;
            ChartCardStore.exportableFieldsCache.set(
                cacheKey,
                exportableFields,
            );
            this.exportableFields = exportableFields;
        } catch (error) {
            this.exportableFields = null;
            this.rootStore
                .getStore(MessageStore)
                .logError("Unable to load exportable fields.");
        } finally {
            this.exportableFieldsLoading = false;
            ChartCardStore.exportableFieldsRequest.delete(this.dataEndpoint);
        }
    }

    /**
     * Builds a CSV blob from grid rows or chart data for client-side export.
     * @returns CSV Blob or null when there is no data to export.
     */
    private buildClientCsvBlob(): Blob | null {
        const gridRows =
            (this.dataGridStore?.rows?.length ?? 0) > 0
                ? this.dataGridStore?.rows ?? []
                : (this.dataGridStore?.allRows?.length ?? 0) > 0
                    ? this.dataGridStore?.allRows ?? []
                    : [];

        if (gridRows.length) {
            const sanitized = gridRows.map((row) => {
                const { id, ...rest } = row as Record<string, unknown>;
                return rest;
            });
            const csv = Papa.unparse(sanitized);
            return new Blob(["\ufeff", csv], { type: "text/csv" });
        }

        if (this.data && this.data.length) {
            const csv = Papa.unparse(this.data);
            return new Blob(["\ufeff", csv], { type: "text/csv" });
        }

        return null;
    }

    // === Initialization helpers ===
    /**
     * Initializes deterministic selection-default resolution and kicks off dependency
     * loading for standalone cards without blocking initial refresh-on-create.
     */
    private initializeSelectionDefaults() {
        if (!this.canAutoRefreshInCurrentContext()) {
            return;
        }

        const resolution = this.resolveSelectionDefaultsForRefresh();

        if (resolution.status === "waiting") {
            this.scheduleStandaloneSelectionDefaultsFollowup(
                resolution.waitFor,
            );
        }
    }

    /**
     * Resolves and applies selection defaults using the pure resolver engine.
     * Dashboard-batch refreshes can defer when required org options are not ready.
     */
    private resolveSelectionDefaultsForRefresh(): SelectionDefaultsRefreshResolution {
        const resolution = resolveSelectionDefaults(
            this.buildSelectionDefaultsResolverContext(),
        );

        if (resolution.status === "waiting") {
            const waitFor = this.requestSelectionDefaultDependencies(
                resolution.dependencies,
            );

            runInAction(() => {
                this.waitingSelectionDefaultDependencies = [
                    ...resolution.dependencies,
                ];
            });

            return {
                status: "waiting",
                waitFor,
            };
        }

        runInAction(() => {
            this.waitingSelectionDefaultDependencies = [];
        });

        this.applyResolvedSelectionDefaults(
            resolution.patch,
            resolution.dataGridSelectionIds,
        );

        this.hasResolvedInitialSelectionDefaults = true;

        return { status: "ready" };
    }

    /**
     * Builds the immutable context used by the pure selection-default resolver.
     */
    private buildSelectionDefaultsResolverContext() {
        const chartTopicOptions = this.topics;
        const topicOptions =
            this.selectionType === "Topics"
                ? chartTopicOptions
                : this.optionsStore.topics.length > 0
                    ? this.optionsStore.topics
                    : chartTopicOptions;
        const vizDefaultSelectedIds = this.normalizeVizDefaultSelectedIds(
            this.parsedVizOptions?.defaultSelectedIds,
        );

        return {
            selectionType: this.selectionType,
            chartType: this.chartType,
            dataEndpoint: this.dataEndpoint,
            maxSelection: this.getMaxSelection(),
            autoSelectionLimit: this.getPageSelectionLimit(),
            preferVizDefaults: !this.hasResolvedInitialSelectionDefaults,
            vizDefaultSelectedIds,
            vizSelectedClassifierCategoryId:
                this.parsedVizOptions?.selectedClassifierCategoryId,
            selectedIds: {
                llmClassifier: this.selectedIds.llmClassifier as
                    | string
                    | undefined,
                classifierCategory: this.selectedIds.classifierCategory as
                    | string
                    | undefined,
                classifiers: this.selectedIds.classifiers as
                    | string
                    | string[]
                    | undefined,
                module: this.selectedIds.module as string | undefined,
                modules: this.selectedIds.modules as
                    | string
                    | string[]
                    | undefined,
                question: this.selectedIds.question as string | undefined,
                questions: this.selectedIds.questions as
                    | string
                    | string[]
                    | undefined,
                tags: this.selectedIds.tags as
                    | string
                    | string[]
                    | undefined,
                topics: this.selectedIds.topics as
                    | string
                    | string[]
                    | undefined,
                themes: this.selectedIds.themes as
                    | string
                    | string[]
                    | undefined,
                classifierGroup: this.selectedIds.classifierGroup as
                    | string
                    | string[]
                    | undefined,
            },
            attemptedDependencies: this.requestedSelectionDefaultDependencies,
            options: {
                orgModules: this.optionsStore.orgModules,
                orgScoredModules: this.optionsStore.orgScoredModules,
                userClassifiers: this.optionsStore.userClassifiers,
                classifierCategories: this.optionsStore.classifierCategories,
                llmClassifiers: this.optionsStore.llmClassifiers,
                customerTypes: this.optionsStore.customerTypes,
                topics: topicOptions,
                themes: this.themes,
                hipaaOptions: this.optionsStore.hipaaOptions,
                extractionLlmClassifiers:
                    this.optionsStore.extractionLlmClassifiers,
                extendedMetadataObjectClassifiers:
                    this.optionsStore.extendedMetadataObjectClassifiers,
            },
            loading: {
                orgModulesLoading: this.optionsStore.orgModulesLoading,
                userClassifiersLoading: this.optionsStore.userClassifiersLoading,
                classifierCategoriesLoading:
                    this.optionsStore.classifierCategoriesLoading,
                llmClassifiersLoading: this.optionsStore.llmClassifiersLoading,
                customerTypesLoading: this.optionsStore.customerTypesLoading,
                hipaaOptionsLoading: this.optionsStore.hipaaOptionsLoading,
                extractionLlmClassifiersLoading:
                    this.optionsStore.extractionLlmClassifiersLoading,
                extendedMetadataObjectClassifiersLoading:
                    this.optionsStore.extendedMetadataObjectClassifiersLoading,
            },
            capabilities: {
                canSelectAllModules: this.config.capabilities.canSelectAllModules,
            },
            moduleSelector: this.config.moduleSelector,
        };
    }

    private applyResolvedSelectionDefaults(
        patch: SelectionDefaultsPatch,
        dataGridSelectionIds?: string[],
    ): boolean {
        let hasSelectionChanges = false;
        const nextSelectedIds = { ...this.selectedIds };

        const applyKey = (
            key: SelectedIdKey,
            value?: string | string[],
        ): void => {
            const currentValue = nextSelectedIds[key];
            if (!this.areSelectionValuesEqual(currentValue, value)) {
                nextSelectedIds[key] = value;
                hasSelectionChanges = true;
            }
        };

        const entries = Object.entries(patch) as Array<
            [SelectedIdKey, string | string[] | undefined]
        >;

        for (const [key, value] of entries) {
            applyKey(key, value);
        }

        // Check for classifierId in the endpoint. Specifically needed for PublishedModelResults charts.
        const llmClassifierId =
            getPublishedModelResultsClassifierIdFromEndpoint(
                this.dataEndpoint,
            );
        if (llmClassifierId) {
            applyKey("llmClassifier", llmClassifierId);
        }

        runInAction(() => {
            this.suppressDefaultPersistence = true;
            try {
                if (hasSelectionChanges) {
                    this.selectedIds = nextSelectedIds;
                }

                if (dataGridSelectionIds !== undefined) {
                    if (this.hasCompletedInitialization) {
                        // During refresh resolution, defer applying grid selections until
                        // postRefresh so labels/series map against the latest rebuilt rows.
                        this.pendingGridSelection = dataGridSelectionIds;
                        this.setVisibleSeries(undefined);
                    } else {
                        this.applyDataGridSelection(dataGridSelectionIds);
                    }
                }
            } finally {
                this.suppressDefaultPersistence = false;
            }
        });

        return hasSelectionChanges;
    }

    private areSelectionValuesEqual(
        left?: string | string[],
        right?: string | string[],
    ): boolean {
        const normalize = (
            value?: string | string[],
        ): { kind: "undefined" | "single" | "multi"; value: string[] } => {
            if (value === undefined) {
                return { kind: "undefined", value: [] };
            }

            if (Array.isArray(value)) {
                return {
                    kind: "multi",
                    value: value.map((id) => `${id}`),
                };
            }

            return {
                kind: "single",
                value: [`${value}`],
            };
        };

        const normalizedLeft = normalize(left);
        const normalizedRight = normalize(right);

        if (normalizedLeft.kind !== normalizedRight.kind) {
            return false;
        }

        if (normalizedLeft.value.length !== normalizedRight.value.length) {
            return false;
        }

        return normalizedLeft.value.every(
            (value, index) => value === normalizedRight.value[index],
        );
    }

    private requestSelectionDefaultDependencies(
        dependencies: SelectionDefaultDependency[],
    ): Promise<void> {
        const uniqueDependencies = Array.from(new Set(dependencies));
        const waitPromises = uniqueDependencies.map((dependency) =>
            this.requestSelectionDefaultDependency(dependency),
        );

        return Promise.all(waitPromises).then(() => undefined);
    }

    private requestSelectionDefaultDependency(
        dependency: SelectionDefaultDependency,
    ): Promise<void> {
        const existingPromise =
            this.selectionDefaultDependencyPromises.get(dependency);
        if (existingPromise) {
            return existingPromise;
        }

        this.requestedSelectionDefaultDependencies.add(dependency);

        const loadPromise = (async () => {
            switch (dependency) {
                case "modules":
                    await this.optionsStore.refreshModules();
                    break;
                case "classifiers":
                    await this.optionsStore.refreshClassifiers();
                    break;
                case "llmClassifiers":
                    await this.optionsStore.refreshLLMClassifiers();
                    break;
                case "customerTypes":
                    await this.optionsStore.refreshCustomerTypes();
                    break;
                case "hipaaOptions":
                    await this.optionsStore.refreshHipaaOptions();
                    break;
                case "extractionLlmClassifiers":
                    await Promise.all([
                        this.optionsStore.refreshSlimExtractionLLMClassifiers(),
                        this.optionsStore.refreshExtendedMetadataObjectClassifiers(),
                    ]);
                    break;
                default:
                    break;
            }
        })()
            .catch(() => undefined)
            .finally(() => {
                this.selectionDefaultDependencyPromises.delete(dependency);
            });

        this.selectionDefaultDependencyPromises.set(dependency, loadPromise);
        return loadPromise;
    }

    private scheduleStandaloneSelectionDefaultsFollowup(
        waitFor: Promise<void>,
    ) {
        if (this.pendingStandaloneDefaultFollowup) {
            return;
        }

        this.pendingStandaloneDefaultFollowup = waitFor.finally(() => {
            this.pendingStandaloneDefaultFollowup = null;
            if (
                !this.isRefreshing &&
                this.canAutoRefreshInCurrentContext()
            ) {
                void this.runRefresh(false, { origin: "standalone" });
            }
        });
    }

    /**
     * Determines the loading state for option dependencies based on selection type.
     * @returns True when required options are still loading.
     */
    private getRequiredOptionsLoadingState() {
        switch (this.selectionType) {
            case "Classifiers":
                return (
                    this.optionsStore.classifierCategoriesLoading ||
                    this.optionsStore.userClassifiersLoading
                );
            case "Question":
            case "Questions":
            case "Tags":
                return this.optionsStore.orgModulesLoading;
            case "Modules":
            case "Module":
                return this.optionsStore.orgModulesLoading;
            case "Topics":
                return false;
            case "LLM Classifier":
            case "LLM Classifier Results":
                return this.optionsStore.llmClassifiersLoading;
            case "Contact Type":
                return this.optionsStore.customerTypesLoading;
            case "HIPAA":
                return this.optionsStore.hipaaOptionsLoading;
            default:
                return false;
        }
    }

    // === Caching & signatures ===
    /**
     * Generates a stable signature for the selected ids, sorted for consistency.
     * @returns JSON string signature of current selections.
     */
    private getSelectionsSignature() {
        const sortedSelections = Object.entries(this.selectedIds ?? {}).sort(
            ([a], [b]) => a.localeCompare(b),
        );

        return this.stringifyNormalizedCacheValue(sortedSelections);
    }

    /**
     * Builds a compare-prev cache key from filter and selection signatures.
     * @param filtersSignature - Serialized filters signature.
     * @param selectionSignature - Serialized selections signature.
     * @returns Combined cache key string.
     */
    private buildComparePrevCacheKey(
        filtersSignature: string,
        selectionSignature: string,
    ) {
        const cacheStrategy = this.config.renderOptions.cacheKeyStrategy;
        const includeFilters = cacheStrategy !== "selectionsChange";
        const includeSelections = cacheStrategy !== "filtersChange";
        const endpointKey = this.dataEndpoint ?? "__endpoint_undefined__";
        const filtersKey = includeFilters ? filtersSignature : "filters-ignored";
        const selectionKey = includeSelections
            ? selectionSignature
            : "selections-ignored";

        return `${endpointKey}::${filtersKey}::${selectionKey}::comparePrev`;
    }

    /**
     * Checks whether formatted chart data has renderable rows/columns/series.
     * @param formatted - Formatted Google Chart data and series names.
     * @returns True when the chart has at least one renderable series.
     */
    private hasRenderableData(
        formatted: { data: any[][]; seriesNames: Set<string> },
    ) {
        const hasRows = formatted.data.length > 1;
        const hasColumns = formatted.data[0]?.length > 1;
        const hasSeries = formatted.seriesNames.size > 0;

        return hasRows && hasColumns && hasSeries;
    }

    /**
     * TotalConversationsDonut always receives a row, even when there are no
     * conversations. Use the endpoint's total-count field to distinguish a
     * renderable donut from that zero-value placeholder.
     */
    private hasRenderableTotalConversationsDonutData(): boolean {
        const data = this.data ? this.filterDataBySelectedIds(this.data) : [];

        return data.some((datum) => {
            const totalConversations = datum.y_totalcount ?? datum.y;

            return (
                typeof totalConversations === "number" &&
                Number.isFinite(totalConversations) &&
                totalConversations > 0
            );
        });
    }

    /**
     * Maximum number of selections allowed based on selection type.
     * @returns Maximum selectable count.
     */
    getMaxSelection = (): number => {
        if (this.selectionType === "Question") {
            return 1;
        }

        return MAX_CHART_SELECTIONS;
    };

    /**
     * One page of selections: the auto-select count, and the cap for data-grid row selection.
     * @returns Page selection count.
     */
    getPageSelectionLimit = (): number => {
        if (this.selectionType === "Question") {
            return 1;
        }

        return this.chartType === "Line"
            ? LINE_CHART_PAGE_SIZE
            : CHART_PAGE_SIZE;
    };

    // === Configuration & grouping ===
    /**
     * Applies local configuration changes.
     * @param params - Partial chart configuration fields to apply.
     * @returns void
     */
    @action
    applyConfigurationChanges({
        title,
        subtitle,
        vizOptions,
    }: {
        title?: string;
        subtitle?: string;
        vizOptions?: SignalsParsedVizOptions;
    }) {
        if (title !== undefined) {
            this.title = title;
        }
        if (subtitle !== undefined) {
            this.subtitle = subtitle;
        }
        if (vizOptions) {
            this.setParsedVizOptions(vizOptions);
            this.hasResolvedInitialSelectionDefaults = false;
        }
    }

    /**
     * Persists chart configuration updates and refreshes data when necessary.
     * @param params - Dashboard id plus title, subtitle, and visualization options.
     * @returns True on success, false when the update fails.
     */
    async updateChartConfiguration({
        dashboardId,
        title,
        subtitle,
        vizOptions,
        objectFilters,
    }: {
        dashboardId: string;
        title?: string;
        subtitle?: string;
        vizOptions?: SignalsParsedVizOptions;
        objectFilters?: string | null;
    }): Promise<boolean> {
        if (!this.dataEndpoint) {
            // If there's no data endpoint, we really shouldn't even get this far
            this.rootStore
                .getStore(MessageStore)
                .logError("Failed to update chart settings.");
            return false;
        }
        let normalizedVizOptions: SignalsParsedVizOptions | undefined;
        if (vizOptions !== undefined) {
            const { showOnlyLocked, ...vizOptionsWithoutShowOnlyLocked } =
                vizOptions as SignalsParsedVizOptions & {
                    showOnlyLocked?: boolean;
                };

            normalizedVizOptions = {
                ...vizOptionsWithoutShowOnlyLocked,
                defaultSelectedIds:
                    showOnlyLocked !== false
                        ? this.normalizeVizDefaultSelectedIds(
                            vizOptionsWithoutShowOnlyLocked.defaultSelectedIds,
                        )
                        : undefined,
            };
        }

        const currentVizSignature = JSON.stringify(
            this.parsedVizOptions ?? {},
        );
        const nextVizSignature = JSON.stringify(normalizedVizOptions ?? {});
        const vizOptionsChanged =
            normalizedVizOptions !== undefined &&
            currentVizSignature !== nextVizSignature;

        try {
            // isEditable means either its a standard dashboard (you can edit those selections and such) OR you have edit permission. isSaveable means you have to have permission to edit.
            // BOTH are required to actually be able to save changes to the backend, otherwise we just apply them locally for the user's current session.
            if (this.isEditable && this.isSaveable) {
                await this.signalsService.updateSignalsChart({
                    id: this.id,
                    dataEndpoint: this.dataEndpoint,
                    title,
                    subtitle,
                    vizOptions: normalizedVizOptions
                        ? JSON.stringify(normalizedVizOptions)
                        : undefined,
                    objectFilters,
                });
            }

            this.applyConfigurationChanges({
                title,
                subtitle,
                vizOptions: vizOptionsChanged ? normalizedVizOptions : undefined,
            });

            return true;
        } catch (error) {
            this.rootStore
                .getStore(MessageStore)
                .logError("Failed to update chart settings.");
            return false;
        }
    }

    /**
     * Updates selection defaults in vizOptions when user selections change.
     * For classifier charts, this also persists selectedClassifierCategoryId.
     * Only persists if both isEditable and isSaveable are true.
     * @returns Promise resolved after update completes or is skipped.
     */
    @action
    async updateDefaultSelectedIds() {
        // Only update if we have permission to edit and save
        if (!this.isEditable || !this.isSaveable) {
            return;
        }

        if (this.suppressDefaultPersistence) {
            return;
        }

        // Avoid overwriting existing viz defaults while async default selection initialization is still in progress.
        if (this.isWaitingOnSelectionDefaults) {
            return;
        }

        // Skip persistence while dependent selections are in a transient unresolved state.
        // This prevents module/question transition churn from writing intermediate empties.
        if (this.selectionType === "Question") {
            if (!this.selectedIds.module || !this.selectedIds.question) {
                return;
            }
        }
        if (this.selectionType === "Questions") {
            if (
                !this.selectedIds.module ||
                !Array.isArray(this.selectedIds.questions)
            ) {
                return;
            }
        }
        if (this.selectionType === "Tags") {
            if (
                !this.selectedIds.module ||
                !this.selectedIds.question ||
                !Array.isArray(this.selectedIds.tags)
            ) {
                return;
            }
        }

        // Get the current selected ids based on selection type
        const currentSelectedIds = this.getSelectedIdsForVizDefaults();

        // Get the default selected ids from viz options
        const defaultSelectedIds = this.normalizeVizDefaultSelectedIds(
            this.parsedVizOptions?.defaultSelectedIds,
        );
        const defaultClassifierCategoryId = normalizeClassifierCategoryId(
            this.parsedVizOptions?.selectedClassifierCategoryId,
        );
        const currentClassifierCategoryId =
            this.selectionType === "Classifiers"
                ? this.getSelectedClassifierCategoryId()
                : defaultClassifierCategoryId;

        // Compare and only update if different
        const arraysEqual = (a: string[], b: string[]) => {
            if (a.length !== b.length) return false;
            const sortedA = [...a].sort();
            const sortedB = [...b].sort();
            return sortedA.every((val, idx) => val === sortedB[idx]);
        };

        const classifierCategoryChanged =
            currentClassifierCategoryId !== defaultClassifierCategoryId;

        const skillGroupingSelector = this.config.skillGroupingSelector;
        const skillGroupingChanged =
            skillGroupingSelector !== undefined &&
            this.selectedSkillGrouping !==
                (this.parsedVizOptions?.skillGrouping ??
                    skillGroupingSelector.defaultValue);

        if (
            arraysEqual(currentSelectedIds, defaultSelectedIds) &&
            !classifierCategoryChanged &&
            !skillGroupingChanged
        ) {
            // No change needed
            return;
        }

        // Update the viz options with new default selections
        const updatedVizOptions: SignalsParsedVizOptions = {
            ...(this.parsedVizOptions ?? {}),
            defaultSelectedIds: currentSelectedIds,
        };

        if (skillGroupingSelector) {
            updatedVizOptions.skillGrouping = this.selectedSkillGrouping;
        }

        if (this.selectionType === "Classifiers") {
            if (currentClassifierCategoryId) {
                updatedVizOptions.selectedClassifierCategoryId =
                    currentClassifierCategoryId;
            } else {
                delete updatedVizOptions.selectedClassifierCategoryId;
            }
        }

        // Persist the change to the backend
        if (!this.dataEndpoint) {
            return;
        }

        try {
            await this.signalsService.updateSignalsChart({
                id: this.id,
                vizOptions: JSON.stringify(updatedVizOptions),
            });

            // Update local state
            this.setParsedVizOptions(updatedVizOptions);
        } catch (error) {
            this.rootStore
                .getStore(MessageStore)
                .logError("Failed to save selection defaults.");
        }
    }

    /**
     * Applies a skill grouping selection and refetches, since the endpoint filters on it. Persistence
     * goes through updateDefaultSelectedIds with the other selections, so a save of an earlier
     * snapshot cannot overwrite it.
     * @param skillGrouping - Individual skills or skill groups.
     */
    @action
    setSkillGrouping(skillGrouping: SkillGrouping) {
        if (skillGrouping === this.selectedSkillGrouping) {
            return;
        }

        this.skillGroupingSelection = skillGrouping;
        // The other grouping is a different set of bars, so start it from the first page.
        this.setPage(0);
        this.handleSelectionChange();
    }

    public override getEffectiveVizOptionsForPersistence():
        | string
        | undefined {
        const baseEffectiveVizOptions =
            super.getEffectiveVizOptionsForPersistence();
        if (!baseEffectiveVizOptions) {
            return undefined;
        }

        const effectiveVizOptions = JSON.parse(
            baseEffectiveVizOptions,
        ) as SignalsParsedVizOptions;

        if (this.selectionType === "Classifiers") {
            const selectedClassifierCategoryId =
                this.getSelectedClassifierCategoryId();

            if (selectedClassifierCategoryId) {
                effectiveVizOptions.selectedClassifierCategoryId =
                    selectedClassifierCategoryId;
            } else {
                delete effectiveVizOptions.selectedClassifierCategoryId;
            }
        }

        if (this.config.skillGroupingSelector) {
            effectiveVizOptions.skillGrouping = this.selectedSkillGrouping;
        }

        return JSON.stringify(effectiveVizOptions);
    }
    private normalizeVizDefaultSelectedIds(value: unknown): string[] {
        if (!Array.isArray(value)) {
            return [];
        }

        return value
            .map((id) => `${id}`.trim())
            .filter((id) => id.length > 0);
    }

    /**
     * The chart's grouping is part of its identity for caching, so it waits for the catalog the
     * same way it waits for any other org data a request is built from.
     */
    protected override loadRefreshPrerequisites(): Promise<void> {
        return this.groupingStore.ensureCatalogLoaded();
    }
}

type SelectionDefaultsRefreshResolution =
    | {
        status: "ready";
    }
    | {
        status: "waiting";
        waitFor: Promise<void>;
    };

type ChartRefreshOptions = BaseRefreshOptions & {
    skipDataGridRefresh?: boolean;
};

type ChartCachePayload = {
    rawResponse: ISignalsChartResponse | null;
    xTitle: string;
    sqlQuery?: string;
    comparePrevData: SignalReportDatum[] | null;
    comparePrevCacheKey: string | null;
};

type SelectedIdKey =
    | "llmClassifier"
    | "classifierCategory"
    | "classifiers"
    | "question"
    | "module"
    | "modules"
    | "questions"
    | "tags"
    | "topics"
    | "themes"
    | "classifierGroup";

export default ChartCardStore;
